import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Navigation, Phone, MessageCircle, Copy, Check, ChevronDown, ChevronUp,
    Loader2, AlertTriangle, FileText, History, MapPin, CalendarDays, SkipForward,
    Trash2, ShieldCheck, Lock, CircleAlert, UserCheck, Paperclip, Flag, Users,
    ClipboardList, ArrowLeft, LocateFixed, ArrowRightLeft, Upload, Eye, Trophy,
    CalendarClock, RotateCcw, Crosshair, ExternalLink, Handshake,
} from 'lucide-react';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import {
    carregarRotaCobranca, listarRotasCobranca, excluirOuCancelarRota,
    abrirAnexoConferencia, cadastrosPorUcId, faturasAtuaisDasParadas, termosPorUc,
    ultimaTratativaPorCpf, reordenarRotaCobrancaAPartirDe, listarEquipeCobranca, transferirRotaCobranca,
    localizarSugestao, removerParada, hidratarCadastrosPeloCache, anexarTermoNasUcs,
    localizarParadasSemPino, pendentesNaFrente, faturasDaParadaHoje, acordosDaParada, lerAcordosVivos,
    acordosVivosPorParada,
    type RotaSalva, type ParadaSalva, type FaturaEmAberto, type FaturaDaParada, type FaturasDaParadaHoje,
    type AcordosDaParada as DadosAcordosDaParada,
} from '../../src/lib/rotaCobranca';
import {
    etiquetaAcordoDaParada, etiquetaDoStatus, papelDasFaturas, type Etiqueta,
} from '../../src/lib/situacaoAcordo.ts';
import AcordosDaParada from './components/AcordosDaParada';
import { COR_TOM } from './components/corTom';
import ConfirmModal from '../../src/components/ConfirmModal';
import { ucsDaParada } from '../../src/lib/agrupamentoRota';
import {
    navUrlCoordenada, suportaRotaEncadeada, APPS_NAVEGACAO, appNavegacaoPreferido, salvarAppNavegacao,
    type AppNavegacao,
} from '../../src/lib/navegacaoMaps';
import { googleMapsTrechosDeAlvos, escolherVizinhas, GOOGLE_MAPS_MAX_STOPS } from '../../src/lib/routeOptimizer';
import { getOrigin, type Origem } from '../../src/lib/rotaInteligente';
import {
    montarMensagemDaRota, type MensagemDaRota,
} from '../../src/lib/mensagemRotaSalva';
import { cabeNoLinkWhatsapp } from '../../src/lib/mensagemCobranca';
import { baseUrlPublica } from '../../src/lib/urlApp';
import ConferenciaRotaModal from '../CRM/Inadimplencia/components/ConferenciaRotaModal';
import ModalAdicionarParada from './ModalAdicionarParada';
import CampoEndereco, { type SugestaoEndereco } from './CampoEndereco';
import LocalizacaoParada from './LocalizacaoParada';
import CriarParcelamentoModal from './CriarParcelamentoModal';
import EscolherQuantasVisitar from './EscolherQuantasVisitar';
import {
    avaliarMaterial, faltasDaFatura, faltasDetalhadas, resumoPendencia, ucsSemTermo, ROTULO_FALTA, ROTULO_CURTO_FALTA,
    type FaltaDetalhada,
} from '../../src/lib/conferenciaRota';
import {
    registrarVisitaCobranca, RESULTADOS_VISITA, iniciarRotaCobranca, concluirRotaCobranca,
    mandarParadaParaOFim, reabrirParadaCobranca, trazerReagendadaParaHoje,
} from '../../src/lib/visitaCobranca';
import { fetchTimelineCobrancaCliente } from '../../src/services/api';
import { copyToClipboard } from '../../src/lib/clipboardShare';
import EnviarWhatsapp from '../../src/components/EnviarWhatsapp';
import { resolverLinkBoleto } from '../../src/lib/linkBoleto';
import { supabase } from '../../src/lib/supabase';
import {
    urlNavegacaoUc, enderecoUcTexto, telefonesUc, linkWhatsappUc, formatarTelefoneBr,
    temCoordenadaConfiavel, destinoNavegacaoUc, consultaGeocodeUc, type UcEndereco,
} from '../../src/lib/enderecoUc';
import { escolherTermo, type ItemTermo, type TermoAdesao } from '../../src/lib/termoUc';
import { dataBr } from '../../src/lib/mensagemCobranca';
import type { CobrancaResultadoContato, EventoTimelineCobranca, UnidadeConsumidora } from '../../types';

// Sob demanda: a ficha da UC é grande e só abre quando falta termo — não pode
// pesar na carga da tela que o cobrador abre no 4G.
const UCManagementModal = React.lazy(() => import('./UCManagementModal'));

const moeda = (v: number | null | undefined) =>
    (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/** AAAA-MM-DD no fuso do aparelho. `toISOString` é UTC: depois das 21h em
 *  Goiás já daria amanhã, e a parada reagendada para hoje sumiria da lista. */
const dataLocal = (d: Date = new Date()): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const diasDepois = (n: number): string => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    return dataLocal(d);
};

/**
 * O recorte da rota que o cobrador escolheu ver — "só quem tem termo" e as
 * paradas escolhidas em "Visitar". Fica no aparelho, por rota: ir ao Maps e
 * voltar recarrega a aba no celular, e o recorte sumia no meio do caminho.
 */
interface VistaRota {
    soComTermo: boolean;
    /** Ids das paradas escolhidas; null = todas. Um conjunto fixo, não "as N
     *  primeiras": visitar uma não pode puxar a seguinte para dentro. */
    escolhidas: string[] | null;
}
const VISTA_PADRAO: VistaRota = { soComTermo: false, escolhidas: null };
const chaveVista = (rotaId: string) => `rotaCobranca:vista:${rotaId}`;
const lerVista = (rotaId: string): VistaRota => {
    try {
        const v = JSON.parse(localStorage.getItem(chaveVista(rotaId)) || 'null');
        return {
            soComTermo: !!v?.soComTermo,
            escolhidas: Array.isArray(v?.escolhidas) ? v.escolhidas.map(String) : null,
        };
    } catch {
        return VISTA_PADRAO;
    }
};
const gravarVista = (rotaId: string, v: VistaRota) => {
    try { localStorage.setItem(chaveVista(rotaId), JSON.stringify(v)); } catch { /* sem storage, vale só nesta aba */ }
};

/** Cadastro da UC lido sob demanda — a parada guarda só o código. */
interface CadastroParada {
    logradouro?: string | null; numero?: string | null; bairro?: string | null;
    cidade?: string | null; estado?: string | null; cep?: string | null; endereco?: string | null;
    latitude?: number | null; longitude?: number | null; geocode_status?: string | null;
    geocode_label?: string | null;
    whatsapp_cliente?: string | null; whatsapp_cliente_2?: string | null;
    titular_fatura?: string | null; outro_numero_uc?: string | null;
    url_termo?: ItemTermo[] | null; documentos_json?: { url?: string; name?: string }[] | null;
    negociacao_id?: number | null;
}

/**
 * A coordenada de onde o cobrador precisa chegar, ou null quando não há uma em
 * que se possa confiar.
 *
 * O pino corrigido na conferência vem primeiro: foi gente olhando o mapa. Depois
 * vale a coordenada do cadastro, e só a confiável — nos níveis 'bairro' e
 * 'cidade' o pino é o centro do município, e navegar até lá leva o cobrador ao
 * lugar errado com cara de certeza (125 clientes plotados no centro, medidos em
 * 20/08/2026). Sem coordenada, a parada fica fora do link da rota e continua
 * acessível pelo botão do próprio card, que usa o endereço em texto.
 */
const pontoDaParada = (
    p: ParadaSalva,
    cadastros: Map<number, UcEndereco>,
): { lat: number; lng: number } | null => {
    if (p.endereco_situacao === 'corrigido' && p.endereco_lat != null && p.endereco_lng != null) {
        return { lat: Number(p.endereco_lat), lng: Number(p.endereco_lng) };
    }
    const cad = p.unidade_consumidora_id != null ? cadastros.get(p.unidade_consumidora_id) : null;
    if (cad && temCoordenadaConfiavel(cad)) return { lat: Number(cad.latitude), lng: Number(cad.longitude) };
    return null;
};

/**
 * O cadastro que o link de navegação da parada usa. O pino corrigido na
 * conferência vale mesmo sem cadastro: a UC que só existe na fatura não tem onde
 * gravar a coordenada, então ela fica só na parada.
 */
const cadastroParaNavegar = (
    p: ParadaSalva,
    cadastro: UcEndereco | null | undefined,
): UcEndereco | null | undefined =>
    p.endereco_situacao === 'corrigido' && p.endereco_lat != null && p.endereco_lng != null
        ? { ...(cadastro ?? {}), latitude: p.endereco_lat, longitude: p.endereco_lng, geocode_status: 'manual' }
        : cadastro;

/**
 * A tela de Faturas já buscando as UCs da parada, em todos os meses. É o que o
 * cobrador fazia na mão (pedido de 30/09/2026): abrir outra aba, ir em
 * Financeiro/Faturas e digitar a UC — ou as UCs, quando a parada junta mais de
 * uma. Várias vão separadas por vírgula, que a busca de Faturas lê como
 * "qualquer uma destas".
 */
const linkFaturasDaParada = (p: ParadaSalva): string => {
    const ucs = Array.from(new Set(ucsDaParada(p).map(u => u.uc_codigo).filter(Boolean)));
    return `#/financeiro/faturas?busca=${encodeURIComponent(ucs.join(', '))}&meses=todos`;
};

/**
 * Tela de campo da rota de cobrança.
 *
 * Mobile-first no molde do OperadorView: coluna estreita, alvos grandes e o que
 * importa acima da dobra. Quem usa isto está na rua, muitas vezes dentro do
 * carro — cada toque a mais é um toque no volante.
 */
const RotaCobrancaView: React.FC = () => {
    const { profile, user } = useAuth();
    const { addToast } = useToast();
    const [params, setParams] = useSearchParams();

    const rotaIdUrl = params.get('rota');
    const paradaUrl = params.get('parada');

    const [rota, setRota] = useState<RotaSalva | null>(null);
    const [outrasRotas, setOutrasRotas] = useState<RotaSalva[]>([]);
    const [carregando, setCarregando] = useState(true);
    const [aberta, setAberta] = useState<string | null>(null);
    const [salvandoApp, setSalvandoApp] = useState(false);
    const [adicionando, setAdicionando] = useState(false);

    // "Do time" é para todo mundo que abre esta tela (pedido de 24/09/2026): a
    // Dhaiane, analista, não via a rota que o resto da cobrança via, e quem
    // divide a rua precisa saber onde o colega já passou. Mexer na rota dos
    // outros — transferir, excluir, reordenar, conferir — segue com quem coordena.
    const coordena = profile?.role === 'adm' || profile?.role === 'gerente';
    const [verDoTime, setVerDoTime] = useState(false);
    const [excluindo, setExcluindo] = useState<string | null>(null);

    // Transferir é de quem coordena — o mesmo corte de quem pode mandar rota
    // para outra pessoa na Central. A equipe só é buscada para quem vai usar.
    const [equipe, setEquipe] = useState<Array<{ id: string; full_name: string | null }>>([]);
    const [transferindo, setTransferindo] = useState<string | null>(null);
    useEffect(() => {
        if (!coordena) return;
        let vivo = true;
        listarEquipeCobranca().then(l => { if (vivo) setEquipe(l); }).catch(() => { /* sem equipe, sem transferir */ });
        return () => { vivo = false; };
    }, [coordena]);
    const podeTransferir = (r: RotaSalva) =>
        coordena && r.status !== 'concluida' && r.status !== 'cancelada';

    /** Atualiza a tela sem reler: reler a lista reabriria sozinha a rota única. */
    const aposTransferir = (rotaId: string, novo: { id: string; nome: string }) => {
        setTransferindo(null);
        const trocar = (r: RotaSalva) => ({ ...r, responsavel_id: novo.id, responsavel_nome: novo.nome });
        // Em "Minhas rotas" a rota passada adiante deixa de ser minha.
        setOutrasRotas(lista => verDoTime
            ? lista.map(r => (r.id === rotaId ? trocar(r) : r))
            : lista.filter(r => r.id !== rotaId));
        setRota(r => (r && r.id === rotaId ? trocar(r) : r));
    };

    const [conferindo, setConferindo] = useState(false);

    // Cadastro de todas as UCs da rota, numa consulta só. Serve ao link da rota
    // inteira no Maps e à mensagem — os dois precisam da rota completa, não de
    // uma parada por vez como os cards.
    const [cadastros, setCadastros] = useState<Map<number, UcEndereco>>(() => new Map());
    /** `chave` é a rota de onde a mensagem saiu — ver `chaveMensagem`. */
    const [mensagem, setMensagem] = useState<(MensagemDaRota & { chave: string }) | null>(null);
    const [montandoMensagem, setMontandoMensagem] = useState(false);
    const [verMensagem, setVerMensagem] = useState(false);

    // Esta tela abre no celular de quem dirige: sem preferência salva, o iPhone
    // cai no Apple Maps e o resto no Google. Ver navegacaoMaps.ts.
    // A escolha fica salva POR NAVEGADOR E POR ENDEREÇO: o hub e o localhost
    // guardam cada um a sua, e antes só dava para trocar na Central — quem abria
    // a rota não sabia por que um mostrava a rota no Maps e o outro não.
    const [appNav, setAppNav] = useState<AppNavegacao>(() => appNavegacaoPreferido());
    const trocarApp = (app: AppNavegacao) => {
        setAppNav(app);
        salvarAppNavegacao(app);
        // A mensagem leva os links do app escolhido: ela se refaz sozinha,
        // porque o app entra em `chaveMensagem`.
    };

    // A rota em aberto única abre direto — mas quem volta para a lista escolheu
    // ver a lista. Sem esta trava, o cobrador com uma rota só nunca chegava na
    // lista nem no "Do time" (caso do Vinicius Alves em 18/09/2026: só via a
    // rota da colega quando alguém mandava o link). É ref, não estado: como
    // dependência do `carregar`, ela disparava uma recarga com a URL velha — o
    // router aplica o setParams depois — e a rota voltava para a tela.
    const escolheuListaRef = useRef(false);
    const voltarParaLista = () => {
        escolheuListaRef.current = true;
        setRota(null);
        setMensagem(null);
        setVerMensagem(false);
        setAberta(null);
        setParams({});
    };

    const [reordenando, setReordenando] = useState(false);
    const [painelReordenar, setPainelReordenar] = useState(false);

    /** Quem coordena limpa a lista inteira; quem montou limpa as próprias — as
     *  duplicatas nascem justamente do clique repetido de quem monta. */
    const podeExcluir = (r: RotaSalva) =>
        coordena || (!!profile?.id && r.criada_por === profile.id);

    /**
     * Apaga a rota, ou cancela quando já tem visita.
     *
     * A lista encheu de duplicatas ("Cobrança GOIÂNIA" duas vezes no mesmo dia) e
     * não havia botão nenhum para limpar — nem policy de DELETE no banco. Quem
     * decide entre apagar e cancelar é `excluirOuCancelarRota`, olhando se alguma
     * parada já foi visitada: visita apagada sumiria da timeline do cliente.
     */
    const removerRota = async (r: RotaSalva) => {
        const comVisita = r.paradas.some(p => p.status !== 'pendente');
        const pergunta = comVisita
            ? `"${r.nome || 'Esta rota'}" já tem visita registrada. Ela será CANCELADA (a visita fica no histórico do cliente). Confirma?`
            : `Excluir "${r.nome || 'esta rota'}" e suas ${r.paradas.length} paradas? Não dá para desfazer.`;
        if (!window.confirm(pergunta)) return;

        setExcluindo(r.id);
        try {
            const oque = await excluirOuCancelarRota(r.id, profile?.id || '', comVisita ? 'Cancelada pelo coordenador.' : null);
            setOutrasRotas(lista => lista.filter(x => x.id !== r.id));
            addToast(oque === 'excluida' ? 'Rota excluída.' : 'Rota cancelada — a visita continua no histórico.', 'success');
        } catch (e: any) {
            addToast(e?.message || 'Falha ao excluir a rota.', 'error');
        } finally {
            setExcluindo(null);
        }
    };

    // Só a resposta do último pedido vale: uma recarga lenta que chega depois
    // da volta para a lista poria a rota de novo na tela.
    const pedidoRef = useRef(0);

    const carregar = useCallback(async () => {
        const pedido = ++pedidoRef.current;
        setCarregando(true);
        try {
            if (rotaIdUrl) {
                const r = await carregarRotaCobranca(rotaIdUrl);
                if (pedido !== pedidoRef.current) return;
                setRota(r);
                if (!r) addToast('Rota não encontrada.', 'error');
            } else if (profile?.id) {
                const lista = await listarRotasCobranca(verDoTime ? null : profile.id, 15);
                if (pedido !== pedidoRef.current) return;
                const emAberto = lista.filter(r => r.status !== 'concluida' && r.status !== 'cancelada');
                setOutrasRotas(lista);
                // Uma rota em aberto só: abre direto, sem obrigar a escolher. Não
                // vale na visão do time — lá o objetivo é justamente comparar —
                // nem depois que a pessoa voltou para a lista por conta própria.
                setRota(!verDoTime && !escolheuListaRef.current && emAberto.length === 1 ? emAberto[0] : null);
            }
        } catch (e: any) {
            if (pedido === pedidoRef.current) addToast(e?.message || 'Falha ao carregar a rota.', 'error');
        } finally {
            if (pedido === pedidoRef.current) setCarregando(false);
        }
    }, [rotaIdUrl, profile?.id, addToast, verDoTime]);

    useEffect(() => { carregar(); }, [carregar]);

    // Deep-link com ?parada=N abre aquele card já expandido.
    useEffect(() => {
        if (!rota || !paradaUrl) return;
        const alvo = rota.paradas.find(p => String(p.posicao) === paradaUrl);
        if (alvo) setAberta(alvo.id);
    }, [rota, paradaUrl]);

    const pendentes = useMemo(
        () => (rota?.paradas || []).filter(p => p.status === 'pendente').length,
        [rota]);

    const totalPendente = useMemo(
        () => (rota?.paradas || [])
            .filter(p => p.status === 'pendente')
            .reduce((s, p) => s + Number(p.valor_total || 0), 0),
        [rota]);

    // Só as UCs mudam o que precisa ser buscado — registrar uma visita troca o
    // objeto da rota inteiro e recarregaria o cadastro à toa.
    const idsDasUcs = (rota?.paradas || [])
        .flatMap(p => ucsDaParada(p).map(u => u.unidade_consumidora_id))
        .join(',');

    /** De quais UCs é o `cadastros` carregado — as faltas só são contadas com ele
     *  pronto, senão toda parada piscaria "sem localização" até o cadastro chegar. */
    const [cadastrosDe, setCadastrosDe] = useState<string | null>(null);
    /** Sobe quando o cadastro das UCs mudou por baixo (localizar em lote). */
    const [versaoCadastros, setVersaoCadastros] = useState(0);

    useEffect(() => {
        const ids = idsDasUcs.split(',').map(Number).filter(n => Number.isFinite(n) && n > 0);
        if (ids.length === 0) { setCadastros(new Map()); setCadastrosDe(idsDasUcs); return; }
        let vivo = true;
        (async () => {
            try {
                // O cache de endereços entra junto, como na conferência: sem ele a UC
                // com coordenada já paga e guardada aparecia "sem localização".
                const mapa = await cadastrosPorUcId(ids).then(hidratarCadastrosPeloCache);
                if (vivo) { setCadastros(mapa); setCadastrosDe(idsDasUcs); }
            } catch {
                // Sem cadastro a tela continua de pé: some o link da rota inteira,
                // e cada card busca o seu quando abre.
            }
        })();
        return () => { vivo = false; };
    }, [idsDasUcs, versaoCadastros]);

    // O que falta em cada parada, para marcar no card FECHADO — antes só se via ao
    // abrir, e quem rolava a lista não sabia que a parada ia sem termo ou sem
    // boleto. É a mesma conta da conferência (`faltasDetalhadas`), numa leva para
    // a rota inteira: faturas e termos saem em lote, não uma busca por card.
    //
    // A chave ignora o status de propósito: registrar uma visita não muda o que
    // falta nas outras paradas e não precisa reler nada.
    const chaveFaltas = (rota?.paradas || [])
        .map(p => `${p.id}:${ucsDaParada(p).map(u => u.uc_codigo).join('+')}:${p.termo_situacao ?? ''}:${p.endereco_lat ?? ''}`)
        .join(',');
    const [infoPorParada, setInfoPorParada] = useState<Map<string, InfoParada>>(() => new Map());
    /** Sobe quando algo fora da parada mudou (termo anexado na UC): relê a leva. */
    const [versaoInfo, setVersaoInfo] = useState(0);
    const recontar = useCallback(() => setVersaoInfo(v => v + 1), []);

    useEffect(() => {
        if (!rota || cadastrosDe !== idsDasUcs) return;
        const paradas = rota.paradas;
        const numerosDaUc = (id: number | null) => {
            const c: any = id != null ? cadastros.get(id) : null;
            return [c?.numero_uc, c?.outro_numero_uc];
        };
        const chaveTermo = (paradaId: string, ucCodigo: string) => `${paradaId}|${ucCodigo}`;
        let vivo = true;
        (async () => {
            try {
                const [faturasPorParada, termos, acordosVivos] = await Promise.all([
                    faturasAtuaisDasParadas(paradas, numerosDaUc),
                    termosPorUc(paradas.flatMap(p => ucsDaParada(p).map(u => ({
                        chave: chaveTermo(p.id, u.uc_codigo),
                        ucId: u.unidade_consumidora_id,
                        cpfCnpj: p.cpf_cnpj_cliente,
                    })))),
                    // A etiqueta de acordo é enfeite do card fechado: sem ela a
                    // lista segue certa, então a falha não derruba o resto.
                    lerAcordosVivos().catch(() => []),
                ]);
                if (!vivo) return;
                const acordosPorParada = acordosVivosPorParada(acordosVivos, paradas, numerosDaUc, faturasPorParada);
                setInfoPorParada(new Map(paradas.map(p => {
                    const cad = p.unidade_consumidora_id != null ? cadastros.get(p.unidade_consumidora_id) : null;
                    const ucs = ucsDaParada(p);
                    // O termo anexado na conferência cobre a parada inteira.
                    const semTermo = p.termo_situacao === 'anexado' ? [] : ucsSemTermo(ucs.map(u => {
                        const c = u.unidade_consumidora_id != null ? cadastros.get(u.unidade_consumidora_id) : null;
                        return {
                            codigo: u.uc_codigo,
                            numerosCadastro: [c?.numero_uc, c?.outro_numero_uc],
                            temTermo: !!termos.get(chaveTermo(p.id, u.uc_codigo))?.url,
                        };
                    }));
                    const faturas = faturasPorParada.get(p.id);
                    return [p.id, {
                        faltas: faltasDetalhadas({
                            temEndereco: !!enderecoUcTexto(cad) || !!p.endereco_label,
                            temCoordenada: (p.endereco_lat ?? cad?.latitude) != null && (p.endereco_lng ?? cad?.longitude) != null,
                            avaliacao: avaliarMaterial(faturas ?? [], p.material_anexos),
                            ucs: ucs.map(u => u.uc_codigo),
                            ucsSemTermo: semTermo,
                        }),
                        ucsSemTermo: ucs
                            .filter(u => semTermo.includes(u.uc_codigo))
                            .map(u => ({ codigo: u.uc_codigo, ucId: u.unidade_consumidora_id })),
                        valorAtual: faturas ? faturas.reduce((s, f) => s + f.valor, 0) : null,
                        acordo: etiquetaAcordoDaParada(acordosPorParada.get(p.id) ?? []),
                    }] as const;
                })));
            } catch {
                // Sem a marcação a lista continua usável; o card aberto mostra tudo.
            }
        })();
        return () => { vivo = false; };
        // `rota` fica fora de propósito: `chaveFaltas` já muda quando o que importa muda.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chaveFaltas, cadastros, cadastrosDe, idsDasUcs, versaoInfo]);

    // As faltas da rota inteira, por tipo. Era um "5 com pendência" solto, e com
    // "Só quem tem termo" ligado 4 das 5 eram justamente as sem termo que a lista
    // escondia — na tela só aparecia 1 (28/09/2026). Maior grupo primeiro.
    const faltasDaRota = useMemo(() => {
        const porRotulo = new Map<string, number[]>();
        for (const p of rota?.paradas || []) {
            if (p.status !== 'pendente') continue;
            for (const f of infoPorParada.get(p.id)?.faltas ?? []) {
                // "sem termo em 1 de 3 UCs" conta junto com "sem termo".
                const rotulo = f.tipo === 'termo' ? ROTULO_CURTO_FALTA.termo : f.curto;
                porRotulo.set(rotulo, [...(porRotulo.get(rotulo) ?? []), p.posicao]);
            }
        }
        return [...porRotulo].sort((a, b) => b[1].length - a[1].length);
    }, [rota, infoPorParada]);

    // Lugar de cada parada pendente no ranking de dívida DA ROTA — pedido de
    // 23/09/2026: saber se a porta é a maior dívida ou a vigésima. Vale o que o
    // cliente deve HOJE (relido na leva acima); enquanto não chega, o congelado.
    const rankingDivida = useMemo(() => {
        const valor = (p: ParadaSalva) => infoPorParada.get(p.id)?.valorAtual ?? Number(p.valor_total || 0);
        const ordem = (rota?.paradas || [])
            .filter(p => p.status === 'pendente')
            .sort((a, b) => valor(b) - valor(a));
        return new Map(ordem.map((p, i) => [p.id, { posicao: i + 1, total: ordem.length }]));
    }, [rota, infoPorParada]);

    // "Rota só de quem tem termo" (pedido de 23/09/2026): sair só pelas portas em
    // que há contrato assinado para mostrar. Filtra o que se dirige — Maps,
    // próxima parada, reordenar, mensagem — e a lista. As outras paradas continuam
    // na rota; só saem da vista. Termo em parte das UCs conta: há contrato a mostrar.
    const [vista, setVista] = useState<VistaRota>(VISTA_PADRAO);
    useEffect(() => { setVista(rota?.id ? lerVista(rota.id) : VISTA_PADRAO); }, [rota?.id]);
    const mudarVista = (muda: Partial<VistaRota>) => {
        const nova = { ...vista, ...muda };
        setVista(nova);
        if (rota) gravarVista(rota.id, nova);
    };
    /** A parada que voltou para a fila (reaberta, trazida para hoje) entra no
     *  recorte escolhido — senão sumiria em "fora da vista" logo depois do clique. */
    const incluirNaVista = (paradaId: string) => {
        if (vista.escolhidas && !vista.escolhidas.includes(paradaId)) {
            mudarVista({ escolhidas: [...vista.escolhidas, paradaId] });
        }
    };
    const soComTermo = vista.soComTermo;

    const temTermo = useCallback((p: ParadaSalva) => {
        const i = infoPorParada.get(p.id);
        return !!i && i.ucsSemTermo.length < ucsDaParada(p).length;
    }, [infoPorParada]);

    // Reagendada para outro dia fica fora da vista até lá (pedido de 24/09/2026):
    // continua pendente na rota, mas não é porta para hoje.
    const hoje = dataLocal();
    const reagendadaFutura = useCallback(
        (p: ParadaSalva) => p.status === 'pendente' && !!p.reagendada_para && p.reagendada_para > hoje,
        [hoje]);
    const reagendadasFuturas = useMemo(
        () => (rota?.paradas || [])
            .filter(reagendadaFutura)
            .sort((a, b) => String(a.reagendada_para).localeCompare(String(b.reagendada_para))),
        [rota, reagendadaFutura]);
    const pendentesDeHoje = useMemo(
        () => (rota?.paradas || []).filter(p => p.status === 'pendente' && !reagendadaFutura(p)),
        [rota, reagendadaFutura]);
    const qtdComTermo = useMemo(() => pendentesDeHoje.filter(temTermo).length, [pendentesDeHoje, temTermo]);

    /** As pendentes que passam no filtro de termo, ainda sem o "Visitar: N". */
    const pendentesFiltradas = useMemo(
        () => pendentesDeHoje.filter(p => !soComTermo || temTermo(p)),
        [pendentesDeHoje, soComTermo, temTermo]);
    const escolhidas = useMemo(
        () => (vista.escolhidas ? new Set(vista.escolhidas) : null), [vista.escolhidas]);

    // "Visitar: N" (pedido de 24/09/2026): as N mais perto umas das outras,
    // partindo da primeira da fila — ver `escolherVizinhas`. Cortar a ordem nas N
    // primeiras deu 559 km para 5 paradas: a ordem de uma rota do estado inteiro
    // dá a volta nele. Para começar perto de onde se está, reordena antes.
    const [escolhendo, setEscolhendo] = useState(false);
    // Para escolher vale até o pino aproximado (centro do bairro): errar a
    // porta por um quarteirão não muda quem é vizinho de quem. O seletor usa a
    // mesma lista para mostrar quem entra antes de confirmar.
    const paraEscolher = useMemo(() => pendentesFiltradas.map(p => {
        const cad = p.unidade_consumidora_id != null ? cadastros.get(p.unidade_consumidora_id) : null;
        const aprox = cad?.latitude != null && cad?.longitude != null
            ? { lat: Number(cad.latitude), lng: Number(cad.longitude) } : null;
        return {
            id: p.id,
            posicao: p.posicao,
            nome: p.cliente_nome || `UC ${p.uc_codigo}`,
            ponto: pontoDaParada(p, cadastros) ?? aprox,
        };
    }), [pendentesFiltradas, cadastros]);
    const escolherQuantas = async (n: number | null) => {
        if (n == null) { mudarVista({ escolhidas: null }); return; }
        const ids = escolherVizinhas(paraEscolher, n);
        mudarVista({ escolhidas: ids });

        // Na ordem da fila elas podiam ir e voltar pela cidade: vão para a frente
        // na ordem do caminho. Muda os números para todo mundo — por isso pergunta,
        // como o "Reordenar".
        if (!rota || !podeReordenar || ids.length < 2) return;
        const jaNaFrente = pendentesDeHoje.slice(0, ids.length).every((p, i) => p.id === ids[i]);
        if (jaNaFrente) return;
        if (!window.confirm(
            `Pôr estas ${ids.length} paradas no começo da fila, na ordem do caminho?\n\n`
            + 'Os números das paradas mudam para todo mundo. Se a mensagem da rota já foi enviada, mande de novo.',
        )) return;
        setEscolhendo(true);
        try {
            await pendentesNaFrente(rota, ids);
            const nova = await carregarRotaCobranca(rota.id);
            if (nova) setRota(nova);
            addToast(`As ${ids.length} paradas estão no começo da fila, na ordem do caminho.`, 'success');
        } catch (e: any) {
            addToast(e?.message || 'Não consegui pôr na ordem. Use "Reordenar a partir de...".', 'error');
        } finally {
            setEscolhendo(false);
        }
    };

    const paradasDaVez = useMemo(() => {
        const naVista = new Set(pendentesFiltradas
            .filter(p => !escolhidas || escolhidas.has(p.id))
            .map(p => p.id));
        return (rota?.paradas || []).filter(p => p.status !== 'pendente' || naVista.has(p.id));
    }, [rota, pendentesFiltradas, escolhidas]);
    const pendentesDaVez = useMemo(() => paradasDaVez.filter(p => p.status === 'pendente'), [paradasDaVez]);

    /** O que ficou fora da lista por escolha — continua na rota. */
    const foraDaVista = [
        soComTermo && pendentesDeHoje.length > qtdComTermo ? `${pendentesDeHoje.length - qtdComTermo} sem termo` : '',
        escolhidas && pendentesFiltradas.length > pendentesDaVez.length
            ? `${pendentesFiltradas.length - pendentesDaVez.length} além das escolhidas` : '',
    ].filter(Boolean);

    /** As paradas que faltam, na ordem, com coordenada boa — é o que o otimizador
     *  consegue ordenar. A posição anda junto, para o reordenar gravar a certa. */
    const pendentesComPonto = useMemo(
        () => pendentesDaVez
            .map(p => ({ id: p.id, posicao: p.posicao, ponto: pontoDaParada(p, cadastros) }))
            .filter((x): x is { id: string; posicao: number; ponto: { lat: number; lng: number } } => !!x.ponto),
        [pendentesDaVez, cadastros]);

    /**
     * As paradas que faltam como o Google Maps as recebe: o pino quando é
     * confiável, senão o mesmo destino do botão "Navegar" do card — em geral o
     * endereço em texto. Antes a parada sem pino ficava fora do link sem aviso:
     * a rota de 16/09/2026 tinha 13 das 24 pendentes assim, e "só quem tem
     * termo (20)" abria um link só.
     */
    const pendentesNoMaps = useMemo(() => pendentesDaVez.flatMap(p => {
        const ponto = pontoDaParada(p, cadastros);
        if (ponto) return [{ id: p.id, posicao: p.posicao, alvo: `${ponto.lat},${ponto.lng}`, porTexto: false }];
        const cad = p.unidade_consumidora_id != null ? cadastros.get(p.unidade_consumidora_id) : null;
        const d = destinoNavegacaoUc(cadastroParaNavegar(p, cad), null, 'google');
        if (!d) return [];
        return [{
            id: p.id,
            posicao: p.posicao,
            alvo: d.tipo === 'coordenada' ? `${d.lat},${d.lng}` : d.texto,
            porTexto: d.tipo === 'texto',
        }];
    }), [pendentesDaVez, cadastros]);
    const foraDoMaps = pendentesDaVez.length - pendentesNoMaps.length;
    const noMapsPorTexto = pendentesNoMaps.filter(x => x.porTexto).length;

    // Localizar em lote o que está sem pino (pedido de 24/09/2026). Quem já foi
    // tentado nesta tela sai da conta: a API devolveria o mesmo "não achei" e
    // cobraria de novo — o que sobra se corrige no card, digitando o endereço.
    const [tentadasLocalizar, setTentadasLocalizar] = useState<Set<string>>(() => new Set());
    const [localizando, setLocalizando] = useState(false);
    /** Pendentes sem pino confiável — vão no Maps pelo texto e ficam fora do reordenar. */
    const semPino = useMemo(() => {
        if (cadastrosDe !== idsDasUcs) return [];
        return (rota?.paradas || []).filter(p => p.status === 'pendente' && !pontoDaParada(p, cadastros));
    }, [rota, cadastros, cadastrosDe, idsDasUcs]);
    /**
     * As que ainda valem uma ida à API. Quem ela já tentou e devolveu 'falhou',
     * 'bairro' ou 'cidade' fica de fora — `geocoded_at` fica no cadastro, então
     * vale depois de recarregar a página. Em 24/09/2026 as mesmas 4 foram pagas
     * três vezes seguidas, sempre com o mesmo "não achei".
     */
    const paraLocalizar = useMemo(() => semPino.filter(p => {
        if (tentadasLocalizar.has(p.id)) return false;
        const cad = p.unidade_consumidora_id != null ? cadastros.get(p.unidade_consumidora_id) : null;
        if (!cad || consultaGeocodeUc(cad).length < 3) return false;
        const jaTentada = !!cad.geocoded_at && ['falhou', 'bairro', 'cidade'].includes(String(cad.geocode_status));
        return !jaTentada;
    }), [semPino, cadastros, tentadasLocalizar]);
    /** O resto se resolve no mapa do card. */
    const marcarNoMapa = useMemo(() => {
        const naBusca = new Set(paraLocalizar.map(p => p.id));
        return semPino.filter(p => !naBusca.has(p.id));
    }, [semPino, paraLocalizar]);

    /** A parada cujo mapa está em modo de correção — uma por vez. */
    const [corrigindoId, setCorrigindoId] = useState<string | null>(null);
    /** Abre o card já no mapa, vindo do atalho do aviso. */
    const abrirParaCorrigir = (paradaId: string) => {
        setAberta(paradaId);
        setCorrigindoId(paradaId);
        // Depois do paint: o card ainda está fechado neste tick.
        requestAnimationFrame(() => document.getElementById(`parada-${paradaId}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    };

    const localizarSemPino = async () => {
        if (paraLocalizar.length === 0) return;
        const alvo = paraLocalizar;
        setLocalizando(true);
        try {
            const res = await localizarParadasSemPino(alvo, cadastros);
            const noEndereco = Array.from(res.porUc.values())
                .filter(r => r.geocode_status === 'endereco' || r.geocode_status === 'rua').length;
            const resto = alvo.length - noEndereco;
            addToast(
                `${noEndereco} de ${alvo.length} localizadas no endereço (US$ ${res.custoUsd.toFixed(2)}).`
                + (resto > 0 ? ` ${resto} a busca não achou: marque no mapa (atalhos no aviso amarelo).` : '')
                + (noEndereco > 0 && podeReordenar ? ' Use "Reordenar a partir de..." para encaixá-las no caminho.' : ''),
                noEndereco > 0 ? 'success' : 'info');
            setVersaoCadastros(v => v + 1);
        } catch (e: any) {
            addToast(e?.message || 'Falha ao localizar os endereços.', 'error');
        } finally {
            setTentadasLocalizar(t => new Set([...t, ...alvo.map(p => p.id)]));
            setLocalizando(false);
        }
    };

    /** A próxima porta: a primeira pendente, na ordem, com link de navegação.
     *  É o que o Apple Maps e o Waze têm no lugar da rota encadeada. */
    const proxima = useMemo(() => {
        for (const p of paradasDaVez) {
            if (p.status !== 'pendente') continue;
            const cad = p.unidade_consumidora_id != null ? cadastros.get(p.unidade_consumidora_id) : null;
            const url = urlNavegacaoUc(cadastroParaNavegar(p, cad), null, appNav);
            if (url) return { posicao: p.posicao, nome: p.cliente_nome || p.uc_codigo, url };
        }
        return null;
    }, [paradasDaVez, cadastros, appNav]);

    // Quem pode mexer na ordem: quem faz a rota, quem montou e quem coordena.
    const podeReordenar = !!rota
        && (rota.status === 'planejada' || rota.status === 'em_execucao')
        && (coordena || rota.responsavel_id === profile?.id || rota.criada_por === profile?.id);

    /**
     * Reordena o que falta a partir de um ponto: o GPS de agora ou um endereço
     * digitado. A ordem da montagem parte de onde quem montou estava, que
     * raramente é de onde o cobrador sai — e nem sempre ele está no ponto de
     * partida quando abre a rota (planeja em casa, sai do escritório).
     * Devolve se reordenou, para o painel saber se fecha.
     */
    const reordenarAPartirDe = async (
        /** Completa "a partir de ..." na confirmação e no aviso final. */
        rotulo: string,
        obterOrigem: () => Promise<Origem | null | undefined>,
        chegada: EscolhaChegada,
    ): Promise<boolean> => {
        if (!rota) return false;
        const qtd = pendentesComPonto.length;
        const fim = chegada === undefined
            ? (rota.destino_label ? ` terminando em ${rota.destino_label}` : '')
            : chegada ? ` terminando em ${chegada.text}` : ' terminando na última parada';
        if (!window.confirm(
            `Reordenar as ${qtd} paradas${escolhidas ? ' escolhidas' : soComTermo ? ' com termo' : ' que faltam'} a partir de ${rotulo}${fim}?\n\n`
            + 'Os números das paradas mudam para todo mundo. Se a mensagem da rota já foi enviada, mande de novo.',
        )) return false;

        setReordenando(true);
        try {
            const origem = await obterOrigem();
            if (!origem) {
                addToast('Não consegui achar esse ponto. Libere a localização no navegador ou digite um endereço.', 'error');
                return false;
            }
            // undefined mantém a chegada gravada; null tira; sugestão vira ponto.
            let pontoChegada: Awaited<ReturnType<typeof localizarSugestao>> | undefined;
            if (chegada === undefined) pontoChegada = undefined;
            else if (chegada === null) pontoChegada = null;
            else pontoChegada = await localizarSugestao(chegada.placeId);
            if (chegada && !pontoChegada) {
                addToast('Não consegui achar o endereço de chegada. Escolha outro.', 'error');
                return false;
            }
            const { reordenadas, distanciaKm } = await reordenarRotaCobrancaAPartirDe(
                rota,
                pendentesComPonto.map(x => ({ id: x.id, ...x.ponto })),
                origem,
                pontoChegada,
            );
            const nova = await carregarRotaCobranca(rota.id);
            // Os números mudaram: a mensagem aberta se refaz na ordem nova
            // (ver `chaveMensagem`).
            if (nova) setRota(nova);
            addToast(
                `Rota reordenada a partir de ${rotulo}${fim}: ${reordenadas} paradas, ${distanciaKm.toFixed(1).replace('.', ',')} km.`,
                'success');
            return true;
        } catch (e: any) {
            addToast(e?.message || 'Falha ao reordenar a rota.', 'error');
            return false;
        } finally {
            setReordenando(false);
        }
    };

    /** A rota em trechos de 10 paradas — limite do Google Maps por URL —, sem
     *  repetir a emenda (ver `googleMapsTrechosDeAlvos`), cada um rotulado com a
     *  posição real da primeira e da última parada. */
    const trechosMaps = useMemo(() => {
        if (!suportaRotaEncadeada(appNav) || pendentesNoMaps.length === 0) return [];
        const destino = rota?.destino_lat != null && rota?.destino_lng != null
            ? { lat: Number(rota.destino_lat), lng: Number(rota.destino_lng) }
            : null;
        return googleMapsTrechosDeAlvos(pendentesNoMaps.map(x => x.alvo), destino, { emendar: false })
            .map(t => ({
                ...t,
                // `ini`/`fim` são 1-based sobre a lista filtrada.
                dePosicao: pendentesNoMaps[t.ini - 1]?.posicao ?? t.ini,
                atePosicao: pendentesNoMaps[t.fim - 1]?.posicao ?? t.fim,
            }));
    }, [pendentesNoMaps, rota?.destino_lat, rota?.destino_lng, appNav]);

    /**
     * Remonta a folha de rota que foi para o WhatsApp do cobrador.
     *
     * Ela só existia no fim da conferência, na Central: quem abria a rota aqui
     * não tinha como recuperá-la, e remontar a rota para ter a mensagem de volta
     * é o que enche a lista de duplicatas. O texto é o mesmo — quem escreve
     * continua sendo `mensagemCobranca.ts` — com o saldo relido na hora.
     */
    /**
     * Tudo que muda o texto: a ordem, quem já foi visitado e o app dos links.
     * Registrar uma visita ou reordenar a partir de onde o cobrador está troca a
     * chave, e a mensagem aberta se refaz — antes ela ficava com a ordem velha e
     * com as portas já batidas, e o cobrador reenviava a folha errada.
     */
    const chaveMensagem = rota
        ? `${rota.id}|${appNav}|${paradasDaVez.map(p => `${p.id}:${p.posicao}:${p.status}`).join(',')}`
        : '';

    // Só vale a resposta do último pedido: uma visita registrada durante a
    // montagem dispara outra, e a mais lenta não pode sobrescrever a nova.
    const pedidoMensagemRef = useRef(0);

    const montarMensagem = async (): Promise<boolean> => {
        if (!rota) return false;
        const pedido = ++pedidoMensagemRef.current;
        const chave = chaveMensagem;
        // A folha vai com o que está na vista: com "só quem tem termo", só essas.
        const rotaDaVez = { ...rota, paradas: paradasDaVez };
        setMontandoMensagem(true);
        try {
            const ids = rotaDaVez.paradas.flatMap(p => ucsDaParada(p).map(u => u.unidade_consumidora_id));
            const cads = cadastros.size > 0 ? cadastros : await cadastrosPorUcId(ids);
            const numerosDaUc = (id: number | null) => {
                const c = id != null ? cads.get(id) : null;
                return [c?.numero_uc, c?.outro_numero_uc];
            };
            // Independentes; em série a espera triplicaria numa rota de 20 paradas.
            const [faturasPorParada, tratativas, termos] = await Promise.all([
                faturasAtuaisDasParadas(rotaDaVez.paradas, numerosDaUc),
                ultimaTratativaPorCpf(rotaDaVez.paradas.map(p => p.cpf_cnpj_cliente)),
                termosPorUc(rotaDaVez.paradas.map(p => ({
                    chave: p.id,
                    ucId: p.unidade_consumidora_id,
                    cpfCnpj: p.cpf_cnpj_cliente,
                }))),
            ]);
            if (pedido !== pedidoMensagemRef.current) return false;
            setMensagem({
                ...montarMensagemDaRota(rotaDaVez, {
                    baseUrl: baseUrlPublica(),
                    app: appNav,
                    cadastros: cads,
                    faturasPorParada,
                    tratativas,
                    termos,
                }),
                chave,
            });
            return true;
        } catch (e: any) {
            if (pedido === pedidoMensagemRef.current) addToast(e?.message || 'Falha ao montar a mensagem.', 'error');
            return false;
        } finally {
            if (pedido === pedidoMensagemRef.current) setMontandoMensagem(false);
        }
    };

    const gerarMensagem = async () => {
        if (mensagem) { setVerMensagem(v => !v); return; }
        if (await montarMensagem()) setVerMensagem(true);
    };

    // A rota mudou por baixo da mensagem. Aberta, ela se refaz; fechada, some —
    // o próximo clique monta de novo, com o que estiver valendo.
    useEffect(() => {
        if (!mensagem || mensagem.chave === chaveMensagem) return;
        if (verMensagem) void montarMensagem();
        else setMensagem(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chaveMensagem]);

    const copiarMensagem = async () => {
        if (!mensagem) return;
        try {
            await copyToClipboard(mensagem.texto);
            addToast('Mensagem copiada.', 'success');
        } catch {
            addToast('Não consegui copiar. Selecione o texto e copie à mão.', 'error');
        }
    };

    if (carregando) {
        return (
            <div className="flex flex-col items-center justify-center py-24">
                <Loader2 size={28} className="animate-spin text-[#5F6C37]" />
                <p className="mt-3 text-sm font-semibold text-gray-500">Carregando a rota...</p>
            </div>
        );
    }

    if (!rota) {
        return (
            <div className="max-w-lg mx-auto px-3 py-8">
                <h1 className="text-lg font-black text-gray-800 dark:text-gray-100">Rota de Cobrança</h1>

                <div className="mt-3 inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                    {[
                        { valor: false, rotulo: 'Minhas rotas' },
                        { valor: true, rotulo: 'Do time' },
                    ].map(op => (
                        <button
                            key={String(op.valor)}
                            onClick={() => { setVerDoTime(op.valor); setRota(null); }}
                            className={`px-3 py-1.5 text-xs font-bold transition-colors ${
                                verDoTime === op.valor
                                    ? 'bg-[#5F6C37] text-white'
                                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                            {op.rotulo}
                        </button>
                    ))}
                </div>

                {outrasRotas.length === 0 ? (
                    <div className="mt-6 p-6 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 text-center">
                        <MapPin size={28} className="mx-auto text-gray-300" />
                        <p className="mt-3 text-sm font-semibold text-gray-600 dark:text-gray-300">
                            {verDoTime ? 'Ninguém do time criou rota ainda.' : 'Nenhuma rota criada ainda.'}
                        </p>
                        <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                            Monte uma na Central de Inadimplência, aba <strong>Visita presencial</strong>:
                            escolha as unidades e clique em "Gerar rota otimizada".
                        </p>
                    </div>
                ) : (
                    <div className="mt-4 space-y-2">
                        <p className="text-xs text-gray-500">{verDoTime ? 'Rotas do time:' : 'Escolha a rota:'}</p>
                        {outrasRotas.map(r => (
                            <div
                                key={r.id}
                                className="group w-full p-3 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-[#5F6C37] transition-colors"
                            >
                                {/* Relativo só ao resumo: o painel de transferir abre embaixo
                                    e não pode arrastar os botões junto. */}
                                <div className="relative">
                                    <button onClick={() => setParams({ rota: r.id })} className="w-full text-left">
                                        <div className="flex items-center justify-between gap-2">
                                            <span className="font-bold text-sm text-gray-800 dark:text-gray-100">
                                                {r.nome || r.cidade || 'Rota de cobrança'}
                                            </span>
                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${
                                                r.status === 'concluida' ? 'bg-gray-100 text-gray-500'
                                                    : r.status === 'em_conferencia' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                                        : 'bg-[#5F6C37]/10 text-[#5F6C37]'}`}>
                                                {r.status === 'concluida' ? 'concluída'
                                                    : r.status === 'em_execucao' ? 'em andamento'
                                                        : r.status === 'em_conferencia' ? 'em conferência' : 'planejada'}
                                            </span>
                                        </div>
                                        <div className="mt-1 flex items-center flex-wrap gap-x-2 gap-y-0.5 text-[11px] text-gray-500 tabular-nums">
                                            <CalendarDays size={11} /> {dataBr(r.data)}
                                            <span>· {r.paradas.length} parada{r.paradas.length === 1 ? '' : 's'}</span>
                                            {r.distancia_km != null && <span>· {Number(r.distancia_km).toFixed(1)} km</span>}
                                        </div>
                                        <div className="pr-16">
                                            <QuemDaRota rota={r} euId={profile?.id} />
                                        </div>
                                    </button>

                                    <div className="absolute -bottom-1 -right-1 flex items-center gap-0.5">
                                        {podeTransferir(r) && (
                                            <button
                                                onClick={() => setTransferindo(transferindo === r.id ? null : r.id)}
                                                title="Transferir para outra pessoa"
                                                aria-expanded={transferindo === r.id}
                                                className={`p-1.5 rounded-lg transition-colors ${transferindo === r.id
                                                    ? 'text-[#5F6C37] bg-[#5F6C37]/10'
                                                    : 'text-gray-300 hover:text-[#5F6C37] hover:bg-[#5F6C37]/10'}`}
                                            >
                                                <ArrowRightLeft size={14} />
                                            </button>
                                        )}
                                        {podeExcluir(r) && (
                                            <button
                                                onClick={() => void removerRota(r)}
                                                disabled={excluindo === r.id}
                                                title="Excluir esta rota"
                                                className="p-1.5 rounded-lg text-gray-300 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-50"
                                            >
                                                {excluindo === r.id
                                                    ? <Loader2 size={14} className="animate-spin" />
                                                    : <Trash2 size={14} />}
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {transferindo === r.id && (
                                    <TransferirRota
                                        rota={r}
                                        equipe={equipe}
                                        onFechar={() => setTransferindo(null)}
                                        onTransferida={novo => aposTransferir(r.id, novo)}
                                    />
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Rota ainda em conferência: o cobrador vê que ela existe e para quem, mas
    // nenhum endereço, telefone ou boleto. É o ponto do pedido — ninguém sai para
    // a rua com material que ninguém olhou. Quem montou continua entrando pela
    // Central, onde a conferência acontece.
    if (rota.status === 'em_conferencia') {
        return (
            <div className="max-w-lg mx-auto px-3 py-8">
                <div className="p-6 rounded-2xl border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/10 text-center">
                    <Lock size={26} className="mx-auto text-amber-600" />
                    <h1 className="mt-3 font-black text-gray-800 dark:text-gray-100">
                        {rota.nome || 'Rota de cobrança'} · aguardando liberação
                    </h1>
                    <p className="mt-2 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                        São {rota.paradas.length} paradas de {dataBr(rota.data)}, mas elas ainda não foram
                        conferidas. Antes de liberar, {rota.criador_nome || 'quem montou a rota'} precisa
                        confirmar o endereço de cada uma e garantir que você tem boleto e fatura para cobrar.
                    </p>
                    <p className="mt-3 text-[11px] text-gray-500">
                        Assim que for liberada ela aparece aqui inteira. Sem isso você chegaria numa porta
                        sem ter como receber.
                    </p>
                </div>
                {/* Quem montou (ou coordena) confere daqui mesmo. Sem isto, fechar a
                    Central antes de terminar deixava a rota travada sem caminho de
                    volta — e o cobrador olhando para um cadeado. */}
                {(coordena || rota.criada_por === profile?.id) && (
                    <button
                        onClick={() => setConferindo(true)}
                        className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 text-white text-sm font-black transition-colors"
                    >
                        <ShieldCheck size={16} /> Conferir agora
                    </button>
                )}
                <button
                    onClick={voltarParaLista}
                    className="mt-2 w-full py-2.5 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm font-bold text-gray-600 dark:text-gray-300"
                >
                    Ver as outras rotas
                </button>

                {conferindo && (
                    <ConferenciaRotaModal
                        rotaId={rota.id}
                        // Relê ao fechar: na conferência dá para tirar e juntar
                        // paradas, e a contagem acima ficaria velha.
                        onFechar={() => { setConferindo(false); void carregar(); }}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-3 pb-16">
            {/* Cabeçalho fixo: o que falta e quanto */}
            <div className="sticky top-0 z-10 -mx-3 px-3 py-3 bg-gray-50/95 dark:bg-gray-900/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
                {/* Sem isto a lista (e o "Do time") só voltava pelo botão do
                    navegador — e nem por ele, quando a rota abriu sozinha. */}
                <button
                    onClick={voltarParaLista}
                    className="-ml-1 mb-1 flex items-center gap-1 px-1 py-0.5 rounded text-[11px] font-bold text-gray-500 hover:text-[#5F6C37] transition-colors"
                >
                    <ArrowLeft size={13} /> Minhas rotas e do time
                </button>
                <h1 className="font-black text-gray-800 dark:text-gray-100 leading-tight">
                    {rota.nome || 'Rota de Cobrança'}
                </h1>
                {/* De quem é e quem montou. Sem isto o coordenador que abre pelo
                    "Do time" não sabe se está na rota certa. */}
                <div className="flex flex-wrap items-center gap-x-2">
                    <QuemDaRota rota={rota} euId={profile?.id} />
                    {podeTransferir(rota) && (
                        <button
                            onClick={() => setTransferindo(transferindo === rota.id ? null : rota.id)}
                            aria-expanded={transferindo === rota.id}
                            className="mt-1 flex items-center gap-1 text-[11px] font-bold text-gray-500 hover:text-[#5F6C37] transition-colors"
                        >
                            <ArrowRightLeft size={11} /> Transferir
                        </button>
                    )}
                </div>
                {transferindo === rota.id && (
                    <TransferirRota
                        rota={rota}
                        equipe={equipe}
                        onFechar={() => setTransferindo(null)}
                        onTransferida={novo => aposTransferir(rota.id, novo)}
                    />
                )}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500 tabular-nums">
                    <span>{dataBr(rota.data)}</span>
                    <span>· {pendentes} de {rota.paradas.length} pendentes</span>
                    <span className="font-bold text-[#5F6C37]">· {moeda(totalPendente)}</span>
                    {faltasDaRota.map(([rotulo, posicoes]) => (
                        <span key={rotulo} title={`Paradas ${posicoes.map(n => `#${n}`).join(', ')}`}
                            className="font-bold text-red-600 dark:text-red-400">
                            · {posicoes.length} {rotulo}
                        </span>
                    ))}
                    {rota.distancia_km != null && <span>· {Number(rota.distancia_km).toFixed(1)} km</span>}
                </div>
                {rota.destino_label && (
                    <div className="mt-0.5 flex items-center gap-1 text-[11px] text-gray-500">
                        <Flag size={11} className="text-[#5F6C37] shrink-0" />
                        <span className="truncate">Termina em {rota.destino_label}</span>
                    </div>
                )}
            </div>

            {/* A rota INTEIRA: navegação encadeada e a folha de rota do WhatsApp.
                As duas só existiam na Central, no fim da conferência — quem abria
                a rota aqui não tinha como chegar nelas, e remontar a rota só para
                ter a mensagem de volta é o que enche a lista de duplicatas. */}
            <div className="mt-3 space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">Abrir no</span>
                        <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                            {APPS_NAVEGACAO.map(a => (
                                <button
                                    key={a.valor}
                                    onClick={() => trocarApp(a.valor)}
                                    aria-pressed={appNav === a.valor}
                                    className={`px-2.5 py-1.5 text-[11px] font-bold transition-colors ${
                                        appNav === a.valor
                                            ? 'bg-[#5F6C37] text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                                >
                                    {a.rotulo}
                                </button>
                            ))}
                        </div>
                    </div>
                    {podeReordenar && pendentesComPonto.length >= 2 && (
                        <button
                            onClick={() => setPainelReordenar(v => !v)}
                            aria-expanded={painelReordenar}
                            title="Refaz a ordem das paradas que faltam partindo de onde você está ou de um endereço"
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition-colors ${painelReordenar
                                ? 'bg-[#5F6C37]/10 border-[#5F6C37] text-[#5F6C37]'
                                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-[#5F6C37]'}`}
                        >
                            {reordenando ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                            Reordenar a partir de...
                            {painelReordenar ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                        </button>
                    )}
                </div>

                {painelReordenar && podeReordenar && pendentesComPonto.length >= 2 && (
                    <ReordenarRota
                        qtd={pendentesComPonto.length}
                        ocupado={reordenando}
                        chegadaAtual={rota.destino_label}
                        onOndeEstou={chegada => {
                            void reordenarAPartirDe('onde você está agora', () => getOrigin(10000), chegada)
                                .then(ok => { if (ok) setPainelReordenar(false); });
                        }}
                        onEndereco={(s, chegada) => {
                            void reordenarAPartirDe(s.text, () => localizarSugestao(s.placeId), chegada)
                                .then(ok => { if (ok) setPainelReordenar(false); });
                        }}
                    />
                )}

                {(qtdComTermo > 0 || pendentesFiltradas.length >= 2 || escolhidas) && (
                    <div className="flex flex-wrap items-center gap-1.5">
                        {qtdComTermo > 0 && (
                            <button
                                onClick={() => { mudarVista({ soComTermo: !soComTermo }); setPainelReordenar(false); }}
                                aria-pressed={soComTermo}
                                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-xs font-bold transition-colors ${soComTermo
                                    ? 'bg-[#5F6C37] border-[#5F6C37] text-white'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:border-[#5F6C37]'}`}
                            >
                                <FileText size={14} />
                                {soComTermo ? `Só quem tem termo (${qtdComTermo}) · ver todas` : `Rota só de quem tem termo (${qtdComTermo})`}
                            </button>
                        )}
                        {(pendentesFiltradas.length >= 2 || escolhidas) && (
                            <EscolherQuantasVisitar
                                paradas={paraEscolher}
                                escolhidas={vista.escolhidas}
                                faltam={pendentesDaVez.length}
                                ocupado={escolhendo}
                                onAbrir={() => setPainelReordenar(false)}
                                onEscolher={n => void escolherQuantas(n)}
                            />
                        )}
                    </div>
                )}
                {foraDaVista.length > 0 && (
                    <p className="text-[11px] text-gray-500">
                        {foraDaVista.join(' e ')} fora da vista — continuam na rota.
                    </p>
                )}

                {/* As "sem localização": primeiro a busca automática, e o que ela não
                    acha vira atalho para o mapa do card. */}
                {semPino.length > 0 && rota.status !== 'concluida' && (
                    <div className="p-2.5 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/50 space-y-2">
                        <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-snug">
                            <strong>{semPino.length} parada{semPino.length > 1 ? 's' : ''} sem pino no mapa.</strong>
                            {' '}Vão no Maps pelo endereço escrito e ficam fora do "Reordenar".
                        </p>
                        {paraLocalizar.length > 0 && (
                            <button
                                onClick={() => void localizarSemPino()}
                                disabled={localizando}
                                title={`Busca no Google e grava no cadastro da UC. Custa uns US$ ${(paraLocalizar.length * 0.005).toFixed(2)}.`}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold transition-colors disabled:opacity-50"
                            >
                                {localizando ? <Loader2 size={14} className="animate-spin" /> : <Crosshair size={14} />}
                                {localizando ? 'Buscando...' : `Buscar ${paraLocalizar.length} no Google`}
                            </button>
                        )}
                        {marcarNoMapa.length > 0 && (
                            <div className="space-y-1.5">
                                <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-snug">
                                    {marcarNoMapa.length === 1 ? 'Esta a busca não achou' : `Estas ${marcarNoMapa.length} a busca não achou`}
                                    {' '}— toque para marcar no mapa:
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                    {marcarNoMapa.map(p => {
                                        const naTela = pendentesDaVez.some(x => x.id === p.id) || reagendadasFuturas.some(x => x.id === p.id);
                                        return (
                                            <button
                                                key={p.id}
                                                onClick={() => naTela
                                                    ? abrirParaCorrigir(p.id)
                                                    : addToast('Esta parada está fora da vista. Mostre todas para abrir.', 'info')}
                                                className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg border text-[11px] font-bold transition-colors ${naTela
                                                    ? 'bg-white dark:bg-gray-800 border-amber-300 dark:border-amber-800 text-amber-900 dark:text-amber-100 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                                                    : 'bg-transparent border-amber-200 dark:border-amber-900 text-amber-700/60 dark:text-amber-300/50'}`}
                                            >
                                                <MapPin size={11} />
                                                #{p.posicao} {(p.cliente_nome || `UC ${p.uc_codigo}`).split(' ')[0]}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex flex-wrap items-center gap-1.5">
                    {/* Apple Maps e Waze não encadeiam paradas: no lugar da rota
                        inteira, a próxima porta num toque. */}
                    {!suportaRotaEncadeada(appNav) && proxima && (
                        <a
                            href={proxima.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors min-w-0"
                        >
                            <Navigation size={14} className="shrink-0" />
                            <span className="truncate">Próxima: #{proxima.posicao} {proxima.nome}</span>
                        </a>
                    )}
                    {trechosMaps.length === 1 && (
                        <a
                            href={trechosMaps[0].url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors"
                        >
                            <Navigation size={14} /> Rota no Maps
                        </a>
                    )}
                    {trechosMaps.length > 1 && (
                        <>
                            <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">
                                Rota no Maps
                            </span>
                            {trechosMaps.map((t, i) => (
                                <a
                                    key={t.url} href={t.url} target="_blank" rel="noopener noreferrer"
                                    title={`Paradas ${t.dePosicao} a ${t.atePosicao}`}
                                    className="flex items-center gap-1 px-2.5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors"
                                >
                                    <Navigation size={13} /> {i + 1}
                                    <span className="text-[10px] font-normal opacity-80 tabular-nums">
                                        ({t.dePosicao}–{t.atePosicao})
                                    </span>
                                </a>
                            ))}
                        </>
                    )}
                    {/* A mensagem leva só o que falta: com tudo visitado não há o que mandar. */}
                    {pendentes > 0 && (
                        <button
                            onClick={gerarMensagem}
                            disabled={montandoMensagem}
                            className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-xs font-bold text-gray-700 dark:text-gray-200 disabled:opacity-50 transition-colors"
                        >
                            {montandoMensagem ? <Loader2 size={14} className="animate-spin" /> : <ClipboardList size={14} />}
                            {montandoMensagem
                                ? (mensagem ? 'Atualizando...' : 'Montando...')
                                : mensagem ? (verMensagem ? 'Esconder a mensagem' : 'Ver a mensagem') : 'Mensagem da rota'}
                        </button>
                    )}
                </div>

                {trechosMaps.length > 1 && (
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                        O Google Maps aceita {GOOGLE_MAPS_MAX_STOPS} paradas por link — é limite do produto.
                        Abra um de cada vez: terminou o 1, abra o 2 — ele sai de onde você estiver.
                    </p>
                )}
                {suportaRotaEncadeada(appNav) && (noMapsPorTexto > 0 || foraDoMaps > 0) && (
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                        {noMapsPorTexto > 0 && `${noMapsPorTexto} vão pelo endereço escrito, sem pino: confira na chegada. `}
                        {foraDoMaps > 0 && `${foraDoMaps} sem endereço ficaram fora do link — abra pelo card.`}
                    </p>
                )}
                {!suportaRotaEncadeada(appNav) && proxima && (
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                        O {APPS_NAVEGACAO.find(a => a.valor === appNav)?.rotulo} não aceita várias paradas num
                        link só — vai uma por vez. Para ver a rota inteira no mapa, escolha Google Maps.
                    </p>
                )}

                {verMensagem && mensagem && pendentes > 0 && (
                    <div className="p-3 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                            {/* Travados enquanto a mensagem se refaz: mandariam a ordem velha. */}
                            <button
                                onClick={copiarMensagem}
                                disabled={montandoMensagem}
                                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-bold transition-colors disabled:opacity-50"
                            >
                                <Copy size={14} /> Copiar
                            </button>
                            <EnviarWhatsapp texto={mensagem.texto} desabilitado={montandoMensagem} />
                        </div>
                        {mensagem.compacta && (
                            <p className="text-[11px] text-gray-500 leading-relaxed">
                                Versão curta: a rota inteira não cabia numa mensagem só. O link de cada
                                parada abre esta tela com endereço, boletos e histórico.
                            </p>
                        )}
                        {!cabeNoLinkWhatsapp(mensagem.texto) && (
                            <p className="flex items-start gap-1.5 text-[11px] text-amber-700 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900/50 rounded-lg px-3 py-2">
                                <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                                <span>
                                    São {mensagem.texto.length} caracteres, grande demais para ir por link.
                                    O botão do WhatsApp abre a janela de compartilhar: escolha o WhatsApp e a
                                    conversa. A mensagem também fica copiada — se não aparecer, cole.
                                </span>
                            </p>
                        )}
                        <textarea
                            readOnly
                            value={mensagem.texto}
                            rows={10}
                            onFocus={e => e.currentTarget.select()}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-900 text-[11px] font-mono bg-gray-50 leading-relaxed outline-none focus:ring-2 focus:ring-[#5F6C37]"
                        />
                    </div>
                )}
            </div>

            <div className="mt-3 space-y-2.5">
                {pendentesDaVez.map(parada => (
                    <CardParada
                        key={parada.id}
                        parada={parada}
                        rotaId={rota.id}
                        info={infoPorParada.get(parada.id)}
                        ranking={rankingDivida.get(parada.id)}
                        onUcMudou={recontar}
                        aberta={aberta === parada.id}
                        appNav={appNav}
                        onAlternar={() => setAberta(aberta === parada.id ? null : parada.id)}
                        onRegistrado={carregar}
                        cadastroMapa={parada.unidade_consumidora_id != null ? cadastros.get(parada.unidade_consumidora_id) : null}
                        corrigindoLocal={corrigindoId === parada.id}
                        onCorrigindoLocal={v => setCorrigindoId(v ? parada.id : null)}
                    />
                ))}
            </div>

            {/* Reagendadas para outro dia: fora da lista de hoje, mas na rota. */}
            {reagendadasFuturas.length > 0 && (
                <details className="mt-4 group border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
                    <summary className="px-4 py-3 cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl transition-colors select-none">
                        <span className="flex items-center gap-1.5">
                            <CalendarClock size={15} className="text-[#5F6C37]" />
                            Reagendadas ({reagendadasFuturas.length})
                        </span>
                        <ChevronDown size={16} className="text-gray-400 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="p-3 border-t border-gray-100 dark:border-gray-700 space-y-2.5 bg-gray-50/50 dark:bg-gray-900/50 rounded-b-xl">
                        <p className="text-[11px] text-gray-500 leading-snug">
                            Voltam sozinhas para a lista no dia marcado. Para ir antes, abra a parada e toque em "Trazer para hoje".
                        </p>
                        {reagendadasFuturas.map(parada => (
                            <CardParada
                                key={parada.id}
                                parada={parada}
                                rotaId={rota.id}
                                info={infoPorParada.get(parada.id)}
                                onUcMudou={recontar}
                                aberta={aberta === parada.id}
                                appNav={appNav}
                                onAlternar={() => setAberta(aberta === parada.id ? null : parada.id)}
                                onRegistrado={carregar}
                                onVoltouParaFila={incluirNaVista}
                                cadastroMapa={parada.unidade_consumidora_id != null ? cadastros.get(parada.unidade_consumidora_id) : null}
                                corrigindoLocal={corrigindoId === parada.id}
                                onCorrigindoLocal={v => setCorrigindoId(v ? parada.id : null)}
                            />
                        ))}
                    </div>
                </details>
            )}

            {rota.status !== 'concluida' && (
                <button
                    onClick={() => setAdicionando(true)}
                    className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-[#5F6C37] text-[#5F6C37] dark:border-[#7a8c47] dark:text-[#7a8c47] bg-white dark:bg-gray-800 hover:bg-[#5F6C37]/5 dark:hover:bg-[#7a8c47]/10 text-sm font-bold transition-colors"
                >
                    + Adicionar parada extra
                </button>
            )}

            {rota.paradas.filter(p => p.status !== 'pendente').length > 0 && (
                <details className="mt-4 group border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800">
                    <summary className="px-4 py-3 cursor-pointer text-sm font-bold text-gray-700 dark:text-gray-200 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-xl transition-colors select-none">
                        <span>Cobranças finalizadas ({rota.paradas.filter(p => p.status !== 'pendente').length})</span>
                        <ChevronDown size={16} className="text-gray-400 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="p-3 border-t border-gray-100 dark:border-gray-700 space-y-2.5 bg-gray-50/50 dark:bg-gray-900/50 rounded-b-xl">
                        {rota.paradas.filter(p => p.status !== 'pendente').map(parada => (
                            <CardParada
                                key={parada.id}
                                parada={parada}
                                rotaId={rota.id}
                                aberta={aberta === parada.id}
                                appNav={appNav}
                                onAlternar={() => setAberta(aberta === parada.id ? null : parada.id)}
                                onRegistrado={carregar}
                                onVoltouParaFila={incluirNaVista}
                            />
                        ))}
                    </div>
                </details>
            )}

            {/* A chegada não é parada, mas é para onde o cobrador vai no fim. */}
            {rota.destino_lat != null && rota.destino_lng != null && (
                <a
                    href={navUrlCoordenada({ lat: Number(rota.destino_lat), lng: Number(rota.destino_lng) }, appNav)}
                    target="_blank" rel="noopener noreferrer"
                    className="mt-3 w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm font-bold text-gray-700 dark:text-gray-200"
                >
                    <Flag size={15} /> Ir para a chegada
                </a>
            )}

            {pendentes === 0 && rota.status !== 'concluida' && (
                <button
                    onClick={async () => {
                        try {
                            await concluirRotaCobranca(rota.id);
                            addToast('Rota concluída.', 'success');
                            carregar();
                        } catch (e: any) { addToast(e?.message || 'Falha ao concluir.', 'error'); }
                    }}
                    className="mt-4 w-full py-3 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-sm font-bold transition-colors"
                >
                    Encerrar a rota
                </button>
            )}

            {adicionando && (
                <ModalAdicionarParada 
                    rotaId={rota.id} 
                    onClose={() => setAdicionando(false)} 
                    onAdicionada={() => {
                        setAdicionando(false);
                        carregar();
                    }}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------

/**
 * A chegada escolhida no painel: `undefined` mantém a que a rota já tem, `null`
 * tira (termina na última parada), sugestão troca.
 */
type EscolhaChegada = SugestaoEndereco | null | undefined;

/**
 * De onde partir ao reordenar: o GPS de agora ou um endereço digitado.
 *
 * O endereço existe porque o GPS nem sempre é o ponto certo — o cobrador
 * planeja em casa ou no escritório antes de sair, ou quer começar por um bairro
 * específico. Quem confirma e grava é `reordenarAPartirDe`; aqui só se escolhe.
 */
const ReordenarRota: React.FC<{
    qtd: number;
    ocupado: boolean;
    /** Onde a rota termina hoje (`destino_label`), ou null. */
    chegadaAtual: string | null;
    onOndeEstou: (chegada: EscolhaChegada) => void;
    onEndereco: (s: SugestaoEndereco, chegada: EscolhaChegada) => void;
}> = ({ qtd, ocupado, chegadaAtual, onOndeEstou, onEndereco }) => {
    const [chegada, setChegada] = useState<EscolhaChegada>(undefined);

    return (
        <div className="p-3 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 space-y-2">
            {/* A chegada vem antes: escolher a partida já reordena. */}
            <p className="text-[11px] text-gray-500 leading-snug">Terminar em (opcional):</p>
            <CampoEndereco
                icone={<Flag size={13} />}
                placeholder="digite o endereço de chegada"
                rotulo="Endereço de chegada da rota"
                valorInicial={chegadaAtual ?? ''}
                disabled={ocupado}
                onEscolher={s => setChegada(s)}
                onLimpar={() => setChegada(null)}
            />

            <p className="pt-1 text-[11px] text-gray-500 leading-snug">
                As {qtd} paradas que faltam vão para a melhor ordem partindo de:
            </p>
            <button
                onClick={() => onOndeEstou(chegada)}
                disabled={ocupado}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-bold disabled:opacity-50 transition-colors"
            >
                {ocupado ? <Loader2 size={14} className="animate-spin" /> : <LocateFixed size={14} />}
                {ocupado ? 'Reordenando...' : 'Onde estou agora'}
            </button>
            <CampoEndereco
                icone={<MapPin size={13} />}
                placeholder="ou digite um endereço de partida"
                rotulo="Endereço de partida para reordenar a rota"
                disabled={ocupado}
                onEscolher={s => onEndereco(s, chegada)}
            />
        </div>
    );
};

/**
 * "Para quem · montada por quem" — sempre os dois.
 *
 * Antes a lista só dizia quem montou, e só quando era outra pessoa; o dono
 * aparecia só na visão do time. Resultado: de sete rotas, uma dizia alguma coisa.
 * Quem faz é quem vai à rua; quem montou é a quem perguntar quando o endereço ou
 * o boleto estiver errado.
 */
const QuemDaRota: React.FC<{ rota: RotaSalva; euId?: string }> = ({ rota, euId }) => {
    const nome = (id: string | null, n: string | null) => (id && id === euId ? 'você' : n || '—');
    return (
        <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px]">
            <UserCheck size={11} className="text-[#5F6C37] shrink-0" />
            <span className="font-semibold text-[#5F6C37]">Para {nome(rota.responsavel_id, rota.responsavel_nome)}</span>
            <span className="text-gray-400">· montada por {nome(rota.criada_por, rota.criador_nome)}</span>
        </div>
    );
};

/**
 * Passa a rota para outra pessoa fazer.
 *
 * Sem isto, quando quem ia não podia ir, o jeito era montar a rota de novo — e
 * refazer a conferência junto. Troca só quem executa: quem montou continua
 * registrado e as visitas já feitas ficam no nome de quem as fez.
 */
const TransferirRota: React.FC<{
    rota: RotaSalva;
    equipe: Array<{ id: string; full_name: string | null }>;
    onFechar: () => void;
    onTransferida: (novo: { id: string; nome: string }) => void;
}> = ({ rota, equipe, onFechar, onTransferida }) => {
    const { addToast } = useToast();
    const [alvo, setAlvo] = useState('');
    const [salvando, setSalvando] = useState(false);
    const opcoes = equipe.filter(p => p.id !== rota.responsavel_id);
    const comVisita = rota.paradas.some(p => p.status !== 'pendente');

    const confirmar = async () => {
        const novo = opcoes.find(p => p.id === alvo);
        if (!novo) return;
        setSalvando(true);
        try {
            await transferirRotaCobranca(rota.id, novo.id);
            const nome = novo.full_name || 'a pessoa escolhida';
            addToast(`Rota transferida para ${nome}.`, 'success');
            onTransferida({ id: novo.id, nome });
        } catch (e: any) {
            addToast(e?.message || 'Falha ao transferir a rota.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className="mt-2 p-2.5 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 space-y-2">
            <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug">
                Quem vai fazer esta rota no lugar de <strong>{rota.responsavel_nome || 'quem está hoje'}</strong>?
                {' '}Quem montou continua registrado{comVisita ? ', e as visitas já feitas ficam no nome de quem fez' : ''}.
            </p>
            <select
                value={alvo}
                onChange={e => setAlvo(e.target.value)}
                aria-label="Nova pessoa responsável pela rota"
                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm outline-none focus:ring-2 focus:ring-[#5F6C37]"
            >
                <option value="">{opcoes.length ? 'Escolha a pessoa...' : 'Carregando a equipe...'}</option>
                {opcoes.map(p => (
                    <option key={p.id} value={p.id}>{p.full_name || p.id}</option>
                ))}
            </select>
            <div className="flex justify-end gap-2">
                <button
                    onClick={onFechar}
                    className="px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-bold text-gray-600 dark:text-gray-300"
                >
                    Cancelar
                </button>
                <button
                    onClick={confirmar}
                    disabled={!alvo || salvando}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-bold disabled:opacity-50 transition-colors"
                >
                    {salvando ? <Loader2 size={13} className="animate-spin" /> : <ArrowRightLeft size={13} />}
                    Transferir
                </button>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------------

/** O que a tela conta em lote para cada parada — ver `infoPorParada`. */
interface InfoParada {
    faltas: FaltaDetalhada[];
    /** As UCs da parada sem termo; `ucId` nulo = só existe na fatura, sem cadastro. */
    ucsSemTermo: Array<{ codigo: string; ucId: number | null }>;
    /** O que o cliente deve hoje; null quando a leitura desta parada falhou. */
    valorAtual: number | null;
    /** O acordo de parcelamento vivo que mais pede atenção, ou null. */
    acordo: Etiqueta | null;
}

interface CardProps {
    parada: ParadaSalva;
    rotaId: string;
    /** Contado em lote pela tela. Ausente = ainda não contou. */
    info?: InfoParada;
    /** Lugar no ranking de dívida das pendentes da rota. */
    ranking?: { posicao: number; total: number };
    /** Algo mudou no cadastro da UC (termo anexado): a tela relê a leva. */
    onUcMudou?: () => void;
    aberta: boolean;
    /** O app escolhido no topo da tela — trocar lá troca o botão de todos os cards. */
    appNav: AppNavegacao;
    onAlternar: () => void;
    onRegistrado: () => void;
    /** A parada voltou para a fila (reaberta, trazida para hoje): a tela a põe
     *  no recorte de "Visitar: N", se houver um. */
    onVoltouParaFila?: (paradaId: string) => void;
    /** Cadastro da UC já com o cache de endereços — o pino que a rota usa. */
    cadastroMapa?: UcEndereco | null;
    /** O mapa desta parada está em modo de correção (controlado pela tela). */
    corrigindoLocal?: boolean;
    onCorrigindoLocal?: (v: boolean) => void;
}

const CardParada: React.FC<CardProps> = ({
    parada, rotaId, info, ranking, onUcMudou, aberta, appNav, onAlternar, onRegistrado, onVoltouParaFila,
    cadastroMapa, corrigindoLocal = false, onCorrigindoLocal,
}) => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const [cadastro, setCadastro] = useState<CadastroParada | null>(null);
    const [faturas, setFaturas] = useState<FaturaEmAberto[] | null>(null);
    // O resto dos boletos da UC (pagos, parcelados, a vencer) e os acordos de
    // parcelamento dela. Pedido de 30/09/2026: para saber se o boleto foi pago ou
    // se já há acordo, o cobrador abria Faturas e Parcelamentos em outra aba.
    const [leitura, setLeitura] = useState<FaturasDaParadaHoje | null>(null);
    const [acordos, setAcordos] = useState<DadosAcordosDaParada | null>(null);
    const [erroAcordos, setErroAcordos] = useState<string | null>(null);
    /** Sobe quando um acordo é criado daqui: relê os acordos da parada. */
    const [versaoAcordos, setVersaoAcordos] = useState(0);
    const [timeline, setTimeline] = useState<EventoTimelineCobranca[] | null>(null);
    const [carregandoDetalhe, setCarregandoDetalhe] = useState(false);
    const [termo, setTermo] = useState<TermoAdesao | null>(null);
    const [verHistorico, setVerHistorico] = useState(false);

    const [registrando, setRegistrando] = useState(false);
    const [resultado, setResultado] = useState<CobrancaResultadoContato | ''>('');
    const [observacao, setObservacao] = useState('');
    const [salvando, setSalvando] = useState(false);
    const [mostrandoParcelamento, setMostrandoParcelamento] = useState(false);
    const [removendo, setRemovendo] = useState(false);
    const [motivoRemocao, setMotivoRemocao] = useState('');
    const [ucAberta, setUcAberta] = useState<UnidadeConsumidora | null>(null);
    const [abrindoUc, setAbrindoUc] = useState<number | null>(null);
    const [anexandoTermo, setAnexandoTermo] = useState(false);
    const inputTermoRef = useRef<HTMLInputElement>(null);
    const [reagendando, setReagendando] = useState(false);
    const [dataVolta, setDataVolta] = useState(() => diasDepois(1));
    const [mexendo, setMexendo] = useState<'reabrir' | 'trazer' | null>(null);

    const visitada = parada.status !== 'pendente';
    const hoje = dataLocal();
    const reagendadaParaDepois = !visitada && !!parada.reagendada_para && parada.reagendada_para > hoje;
    const rotuloResultado = (r: string | null) =>
        (r && RESULTADOS_VISITA.find(x => x.valor === r)?.rotulo) || r || '';
    const dataDaVisita = parada.visitado_em ? new Date(parada.visitado_em).toLocaleDateString('pt-BR') : null;

    // O que falta para cobrar esta UC. `material_anexos` entra na conta: o PDF
    // anexado na conferência vale como fatura da Equatorial, senão a linha
    // apareceria "sem material" depois de alguém já ter resolvido.
    const avaliacaoMaterial = useMemo(
        () => avaliarMaterial(faturas ?? [], parada.material_anexos),
        [faturas, parada.material_anexos]);

    // Detalhe só é buscado quando o card abre: numa rota de 20 paradas, carregar
    // tudo de saída seriam 60 requisições no 4G do carro.
    // `carregandoDetalhe` NÃO entra nas dependências. Entrando, o efeito virava um
    // laço que se cancelava: setCarregandoDetalhe(true) re-disparava o efeito, o
    // cleanup marcava `vivo = false`, a busca em voo era descartada sem gravar e o
    // `finally` não desligava o loading (porque `vivo` já era falso). O card ficava
    // em "Carregando..." para sempre. `faturas !== null` é a única trava de que o
    // efeito precisa — ela já significa "este card já buscou".
    useEffect(() => {
        if (!aberta || faturas !== null) return;
        let vivo = true;
        (async () => {
            setCarregandoDetalhe(true);
            try {
                const { data: uc } = await supabase
                    .from('unidades_consumidoras')
                    .select('logradouro, numero, bairro, cidade, estado, cep, endereco, latitude, longitude, geocode_status, geocode_label, whatsapp_cliente, whatsapp_cliente_2, titular_fatura, numero_uc, outro_numero_uc, url_termo, documentos_json, negociacao_id')
                    .eq('id', parada.unidade_consumidora_id ?? -1)
                    .maybeSingle();
                if (!vivo) return;
                setCadastro((uc as CadastroParada) ?? null);

                // O contrato assinado. A UC quase nunca tem (8 de 345 medidas em
                // 10/09/2026); quem costuma ter e a negociacao vinculada — 44 de
                // 44 das que tem vinculo. Por isso a segunda consulta.
                let doTermo = escolherTermo((uc as any)?.url_termo);
                const negId = (uc as any)?.negociacao_id;
                if (!doTermo && negId != null) {
                    const { data: neg } = await supabase
                        .from('negociacoes').select('url_termo').eq('id', negId).maybeSingle();
                    doTermo = escolherTermo((neg as any)?.url_termo);
                }
                if (vivo) setTermo(doTermo);

                // Parada que junta UCs do mesmo cliente: as faturas de todas. As
                // agrupadas precisam do próprio cadastro para o segundo número.
                const extras = ucsDaParada(parada).slice(1).map(u => u.unidade_consumidora_id);
                const outros = await cadastrosPorUcId(extras);
                const numerosDaUc = (id: number | null) => {
                    if (id == null) return [];
                    const cad: any = id === parada.unidade_consumidora_id ? uc : outros.get(id);
                    return [cad?.numero_uc, cad?.outro_numero_uc];
                };
                const hojeUc = await faturasDaParadaHoje(parada, numerosDaUc);
                if (!vivo) return;
                // As duas no mesmo passo: `faturas` é a trava deste efeito, e
                // gravá-la antes cancelaria a leitura com `leitura` ainda vazia.
                setLeitura(hojeUc);
                setFaturas(hojeUc.vencidas);
            } catch (e: any) {
                if (vivo) { addToast(e?.message || 'Falha ao carregar a parada.', 'error'); setFaturas([]); }
            } finally {
                if (vivo) setCarregandoDetalhe(false);
            }
        })();
        return () => { vivo = false; };
    }, [aberta, faturas, parada, addToast]);

    // Os acordos vêm depois das faturas e não seguram o card: o cobrador já vê o
    // que cobrar enquanto eles chegam.
    useEffect(() => {
        if (!leitura) return;
        let vivo = true;
        setErroAcordos(null);
        acordosDaParada(parada, leitura)
            .then(a => { if (vivo) setAcordos(a); })
            .catch((e: any) => { if (vivo) setErroAcordos(e?.message || 'falha na leitura'); });
        return () => { vivo = false; };
        // `parada` fica de fora: `leitura` já é desta parada, e a rota relida a
        // cada visita registrada traria um objeto novo — e uma consulta à toa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leitura, versaoAcordos]);

    /** Que papel cada fatura tem num acordo vivo (entrada, parcela, parcelada). */
    const papelNoAcordo = useMemo(
        () => (acordos ? papelDasFaturas(acordos.acordos) : new Map<string, Etiqueta>()),
        [acordos]);

    const carregarHistorico = async () => {
        setVerHistorico(true);
        if (timeline !== null || !parada.cpf_cnpj_cliente) return;
        try {
            setTimeline(await fetchTimelineCobrancaCliente(parada.cpf_cnpj_cliente));
        } catch { setTimeline([]); }
    };

    const valorAtual = faturas?.reduce((s, f) => s + f.valor, 0) ?? null;
    const congelado = Number(parada.valor_total || 0);
    // Diferença acima de um real: abaixo disso é arredondamento, não pagamento.
    const mudou = valorAtual != null && Math.abs(valorAtual - congelado) > 1;

    const endereco = enderecoUcTexto(cadastro) || '—';
    const urlMaps = urlNavegacaoUc(cadastroParaNavegar(parada, cadastro), null, appNav);
    const telefones = telefonesUc(cadastro);
    const zap = linkWhatsappUc(cadastro);

    const copiar = async (texto: string, oque: string) => {
        try { await copyToClipboard(texto); addToast(`${oque} copiado.`, 'success'); }
        catch { addToast('Não consegui copiar.', 'error'); }
    };

    /** PDF anexado na conferência (fatura da Equatorial ou termo) — bucket privado. */
    const abrirAnexo = async (caminho: string) => {
        try { await abrirAnexoConferencia(caminho); }
        catch (e: any) { addToast(e?.message || 'Não consegui abrir o anexo.', 'error'); }
    };

    const fecharRegistro = () => {
        setRegistrando(false);
        setReagendando(false);
        setResultado(''); setObservacao('');
    };

    /**
     * Registra a visita. 'reagendar' (pedido de 24/09/2026) também vale como
     * visita — entra na timeline —, mas a parada continua pendente nesta rota,
     * vai para o fim da fila e some da lista até o dia marcado.
     */
    const registrar = async (acao: 'visitado' | 'pulado' | 'reagendar') => {
        if (!profile?.id) return;
        if (!resultado) { addToast('Escolha o que aconteceu.', 'error'); return; }
        if (!observacao.trim()) { addToast('Descreva o que foi tratado.', 'error'); return; }
        const reagendarPara = acao === 'reagendar' ? dataVolta : null;
        if (acao === 'reagendar' && (!dataVolta || dataVolta < hoje)) {
            addToast('Escolha o dia de voltar — hoje ou depois.', 'error');
            return;
        }
        setSalvando(true);
        try {
            await iniciarRotaCobranca(rotaId);
            const coords = await new Promise<{ lat: number; lng: number } | null>(resolve => {
                if (!navigator.geolocation) return resolve(null);
                navigator.geolocation.getCurrentPosition(
                    p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
                    () => resolve(null),
                    { timeout: 5000 });
            });

            const res = await registrarVisitaCobranca({
                paradaId: parada.id,
                cpfCnpjCliente: parada.cpf_cnpj_cliente,
                clienteNome: parada.cliente_nome,
                resultado: resultado as CobrancaResultadoContato,
                observacao,
                userId: profile.id,
                userNome: profile.full_name || 'Usuário',
                coords,
                status: acao === 'reagendar' ? undefined : acao,
                reagendarPara,
            });

            if (reagendarPara) {
                // A visita já está gravada: falhar aqui não pode parecer que ela se perdeu.
                try {
                    await mandarParadaParaOFim(rotaId, parada.id);
                } catch {
                    addToast('A parada não foi para o fim da fila. Use "Reordenar a partir de..." se ela atrapalhar.', 'info');
                }
                addToast(reagendarPara === hoje
                    ? 'Visita registrada. A parada foi para o fim da fila de hoje.'
                    : `Visita registrada. A parada volta para a lista em ${dataBr(reagendarPara)}.`, 'success');
            } else {
                addToast('Visita registrada.', 'success');
            }
            if (!res.naTimeline && res.motivoForaDaTimeline) addToast(res.motivoForaDaTimeline, 'info');
            fecharRegistro();
            onRegistrado();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao registrar a visita.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    /** Visitada ou pulada volta para a lista, no fim da fila — ver `reabrirParadaCobranca`. */
    const reabrir = async () => {
        if (!window.confirm(
            `Reabrir a parada ${parada.posicao} (${parada.cliente_nome || `UC ${parada.uc_codigo}`})?\n\n`
            + 'Ela volta para a lista, no fim da fila. O que foi registrado continua no histórico do cliente.',
        )) return;
        setMexendo('reabrir');
        try {
            await reabrirParadaCobranca(parada.id, rotaId);
            addToast('Parada reaberta: está no fim da fila.', 'success');
            onVoltouParaFila?.(parada.id);
            onRegistrado();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao reabrir a parada.', 'error');
        } finally {
            setMexendo(null);
        }
    };

    const trazerParaHoje = async () => {
        setMexendo('trazer');
        try {
            await trazerReagendadaParaHoje(parada.id);
            addToast('A parada voltou para a lista de hoje.', 'success');
            onVoltouParaFila?.(parada.id);
            onRegistrado();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao trazer a parada.', 'error');
        } finally {
            setMexendo(null);
        }
    };

    /** A ficha completa da UC (UCManagementModal), aberta na aba do termo. */
    const abrirUc = async (ucId: number) => {
        setAbrindoUc(ucId);
        try {
            const { data, error } = await supabase
                .from('unidades_consumidoras').select('*').eq('id', ucId).maybeSingle();
            if (error) throw error;
            if (!data) { addToast('UC não encontrada.', 'error'); return; }
            setUcAberta(data as UnidadeConsumidora);
        } catch (e: any) {
            addToast(e?.message || 'Não consegui abrir a UC.', 'error');
        } finally {
            setAbrindoUc(null);
        }
    };

    const fecharUc = () => { setUcAberta(null); onUcMudou?.(); };

    const anexarTermo = async (arquivo: File | undefined) => {
        const alvos = info?.ucsSemTermo ?? [];
        if (!arquivo || alvos.length === 0) return;
        setAnexandoTermo(true);
        try {
            const { url } = await anexarTermoNasUcs(parada.id, alvos, arquivo);
            setTermo({ url, dataAssinatura: null, numero: null, validado: false, verificada: null });
            addToast('Termo anexado na UC.', 'success');
            // Sem cadastro, o termo foi para a parada: é ela que precisa ser relida.
            if (alvos.some(u => u.ucId == null)) onRegistrado();
            else onUcMudou?.();
        } catch (e: any) {
            addToast(e?.message || 'Falha ao anexar o termo.', 'error');
        } finally {
            setAnexandoTermo(false);
            if (inputTermoRef.current) inputTermoRef.current.value = '';
        }
    };

    const fecharRemocao = () => { setRemovendo(false); setMotivoRemocao(''); };

    const handleRemoverParada = async () => {
        if (!profile?.id || !motivoRemocao.trim()) return;
        setSalvando(true);
        try {
            await removerParada(parada.id, rotaId, motivoRemocao, profile.id);
            addToast('Parada removida da rota.', 'success');
            fecharRemocao();
            onRegistrado();
        } catch (e: any) {
            addToast(e?.message || 'Erro ao remover.', 'error');
        } finally {
            setSalvando(false);
        }
    };

    // Só na pendente: a visitada já foi, a falta não muda mais nada.
    const faltasVisiveis = !visitada ? (info?.faltas ?? []) : [];
    const semTermo = !visitada ? (info?.ucsSemTermo ?? []) : [];
    const aviso = !visitada && parada.material_situacao === 'justificado'
        ? String(parada.material_justificativa || '').trim() : '';

    return (
        <div id={`parada-${parada.id}`} className={`scroll-mt-44 rounded-2xl border overflow-hidden transition-colors ${visitada
            ? 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'}${
            faltasVisiveis.length > 0 ? ' border-l-4 border-l-red-500 dark:border-l-red-500' : ''}`}>

            {/* Resumo — sempre visível. O botão que abre e fecha cobre o cabeçalho
                inteiro por baixo; com o card aberto, o nome fica por cima dele como
                link para as faturas (link dentro de botão é HTML inválido). */}
            <div className="relative p-3 flex items-start gap-2.5">
                <button
                    type="button"
                    onClick={onAlternar}
                    aria-expanded={aberta}
                    aria-label={`${aberta ? 'Fechar' : 'Abrir'} a parada ${parada.posicao}: ${parada.cliente_nome || `UC ${parada.uc_codigo}`}`}
                    className="absolute inset-0 w-full h-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#5F6C37]"
                />
                <span className={`shrink-0 w-7 h-7 rounded-full text-xs font-black flex items-center justify-center tabular-nums ${visitada ? 'bg-gray-200 text-gray-500 dark:bg-gray-700' : 'bg-[#5F6C37] text-white'}`}>
                    {visitada ? <Check size={14} /> : parada.posicao}
                </span>
                <div className="flex-1 min-w-0">
                    {aberta ? (
                        // Pedido de 30/09/2026: era abrir outra aba, ir em Faturas e
                        // digitar a UC. O link já leva com as UCs da parada buscadas.
                        <a
                            href={linkFaturasDaParada(parada)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Abrir as faturas desta parada no Financeiro"
                            className="relative z-10 inline-flex items-start gap-1 font-bold text-sm leading-tight text-[#5F6C37] dark:text-[#9fb567] underline decoration-dotted underline-offset-2 hover:decoration-solid"
                        >
                            <span>{parada.cliente_nome || parada.uc_codigo}</span>
                            <ExternalLink size={12} className="shrink-0 mt-0.5" />
                        </a>
                    ) : (
                        <div className="font-bold text-sm text-gray-800 dark:text-gray-100 leading-tight">
                            {parada.cliente_nome || parada.uc_codigo}
                        </div>
                    )}
                    <div className="text-[11px] text-gray-500 tabular-nums">
                        UC {parada.uc_codigo}{ucsDaParada(parada).length > 1 && ` +${ucsDaParada(parada).length - 1}`}
                        {' · '}{parada.qtd_faturas} fatura{Number(parada.qtd_faturas) > 1 ? 's' : ''}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="font-black text-sm text-[#5F6C37] tabular-nums">{moeda(congelado)}</span>
                        {!visitada && ranking && (
                            <span title={`${ranking.posicao}ª maior dívida entre as ${ranking.total} paradas que faltam`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] leading-none font-black tabular-nums bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:ring-amber-800">
                                <Trophy size={10} /> {ranking.posicao}ª maior dívida
                            </span>
                        )}
                        {!visitada && parada.reagendada_para && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] leading-none font-black bg-blue-50 text-blue-800 ring-1 ring-inset ring-blue-200 dark:bg-blue-900/30 dark:text-blue-200 dark:ring-blue-800">
                                <CalendarClock size={10} />
                                {reagendadaParaDepois ? `Volta em ${dataBr(parada.reagendada_para)}` : 'Reagendada: voltar hoje'}
                            </span>
                        )}
                        {!visitada && !parada.reagendada_para && parada.visitado_em && (
                            <span title="Já foi visitada e foi reaberta"
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] leading-none font-black bg-gray-100 text-gray-600 ring-1 ring-inset ring-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:ring-gray-600">
                                <RotateCcw size={10} /> Revisita
                            </span>
                        )}
                        {info?.acordo && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] leading-none font-black ring-1 ring-inset ${COR_TOM[info.acordo.tom].chip}`}>
                                <Handshake size={10} /> {info.acordo.rotulo}
                            </span>
                        )}
                    </div>
                    {faltasVisiveis.length > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1">
                            {faltasVisiveis.map(f => (
                                <span key={f.tipo} title={f.detalhe}
                                    className="px-1.5 py-0.5 rounded text-[10px] leading-none font-black uppercase tracking-wide bg-red-50 text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/40 dark:text-red-300 dark:ring-red-900">
                                    {f.curto}
                                </span>
                            ))}
                        </div>
                    )}
                    {/* Quem liberou disse por que a parada saiu assim e quem resolve. */}
                    {aviso && faltasVisiveis.length > 0 && (
                        <p className="mt-1 flex items-start gap-1 text-[10.5px] leading-snug text-amber-700 dark:text-amber-300">
                            <CircleAlert size={11} className="shrink-0 mt-px" />
                            <span className="line-clamp-2">{aviso}</span>
                        </p>
                    )}
                </div>
                {aberta ? <ChevronUp size={18} className="text-gray-400 shrink-0" /> : <ChevronDown size={18} className="text-gray-400 shrink-0" />}
            </div>

            {aberta && (
                <div className="px-3 pb-3 space-y-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                    {carregandoDetalhe && (
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Loader2 size={14} className="animate-spin" /> Carregando...
                        </div>
                    )}

                    {/* Quem volta à porta precisa saber o que já foi dito lá. */}
                    {!visitada && (parada.visitado_em || parada.reagendada_para) && (
                        <div className="p-2.5 rounded-lg bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900 space-y-1">
                            <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-blue-800 dark:text-blue-200">
                                <History size={12} /> Última visita{dataDaVisita ? ` · ${dataDaVisita}` : ''}
                            </p>
                            {(parada.resultado || parada.observacao) && (
                                <p className="text-[11px] leading-snug text-blue-900 dark:text-blue-100">
                                    {parada.resultado && <strong>{rotuloResultado(parada.resultado)}</strong>}
                                    {parada.resultado && parada.observacao && ' — '}
                                    {parada.observacao}
                                </p>
                            )}
                            {parada.reagendada_para && (
                                <p className="text-[11px] text-blue-800 dark:text-blue-200">
                                    {reagendadaParaDepois
                                        ? `Reagendada: volta para a lista em ${dataBr(parada.reagendada_para)}.`
                                        : 'Reagendada para hoje.'}
                                </p>
                            )}
                            {reagendadaParaDepois && (
                                <button onClick={() => void trazerParaHoje()} disabled={mexendo !== null}
                                    className="mt-1 flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors disabled:opacity-50">
                                    {mexendo === 'trazer' ? <Loader2 size={13} className="animate-spin" /> : <CalendarClock size={13} />}
                                    Trazer para hoje
                                </button>
                            )}
                        </div>
                    )}

                    {/* O carimbo do resumo, dito por extenso: o que falta e de qual UC ou fatura. */}
                    {faltasVisiveis.length > 0 && (
                        <div className="p-2.5 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 space-y-1">
                            <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-red-700 dark:text-red-300">
                                <CircleAlert size={12} /> O que falta nesta parada
                            </p>
                            <ul className="space-y-0.5">
                                {faltasVisiveis.map(f => (
                                    <li key={f.tipo} className="text-[11px] leading-snug text-red-800 dark:text-red-200">• {f.detalhe}</li>
                                ))}
                            </ul>
                            {faltasVisiveis.some(f => f.tipo === 'endereco') && !corrigindoLocal && (
                                <div className="pt-1.5">
                                    <button onClick={() => onCorrigindoLocal?.(true)}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold transition-colors">
                                        <Crosshair size={12} /> Marcar no mapa
                                    </button>
                                </div>
                            )}
                            {/* Sem termo: resolver daqui mesmo, sem sair da rota. */}
                            {semTermo.length > 0 && (
                                <div className="pt-1.5 flex flex-wrap gap-1.5">
                                    <input ref={inputTermoRef} type="file" accept="application/pdf" className="hidden"
                                        onChange={e => void anexarTermo(e.target.files?.[0])} />
                                    <button onClick={() => inputTermoRef.current?.click()} disabled={anexandoTermo}
                                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-[11px] font-bold transition-colors disabled:opacity-50">
                                        {anexandoTermo ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
                                        Anexar termo
                                    </button>
                                    {semTermo.filter(u => u.ucId != null).map(u => (
                                        <button key={u.codigo} onClick={() => void abrirUc(u.ucId!)} disabled={abrindoUc === u.ucId}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40 text-[11px] font-bold transition-colors disabled:opacity-50">
                                            {abrindoUc === u.ucId ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />}
                                            Ver UC{semTermo.length > 1 ? ` ${u.codigo}` : ''}
                                        </button>
                                    ))}
                                    {semTermo.some(u => u.ucId == null) && (
                                        <p className="w-full text-[10.5px] leading-snug text-red-700/80 dark:text-red-300/80">
                                            UC {semTermo.filter(u => u.ucId == null).map(u => u.codigo).join(', ')} não tem cadastro:
                                            {' '}o termo anexado fica guardado nesta parada.
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {mudou && (
                        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-snug">
                                O valor mudou desde que a rota foi montada. Estava {moeda(congelado)},
                                agora são <strong>{moeda(valorAtual)}</strong>.
                                {Number(valorAtual) > 0 ? ' Cobre o valor de hoje.' : ' Veja abaixo o que foi pago ou parcelado.'}
                            </p>
                        </div>
                    )}

                    {/* Cliente com mais de uma UC em débito: cobra tudo nesta porta. */}
                    {ucsDaParada(parada).length > 1 && (
                        <div className="p-2.5 rounded-lg bg-[#5F6C37]/[0.07] border border-[#5F6C37]/20 space-y-1">
                            <p className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-[#5F6C37]">
                                <Users size={12} /> Cobrar aqui {ucsDaParada(parada).length} UCs
                            </p>
                            {ucsDaParada(parada).map((u, i) => (
                                <div key={u.uc_codigo} className="flex items-center justify-between gap-2 text-[11px] tabular-nums">
                                    <span className="text-gray-700 dark:text-gray-200">
                                        UC {u.uc_codigo}{i === 0 && <span className="text-gray-400"> · este endereço</span>}
                                    </span>
                                    <span className="font-bold text-gray-800 dark:text-gray-100">{moeda(u.valor_total)}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Endereço + navegar */}
                    <div>
                        <p className="text-[11px] text-gray-600 dark:text-gray-300 leading-snug">{endereco}</p>
                        {cadastro && !urlMaps && (
                            <p className="text-[11px] text-red-600 font-semibold mt-1">Sem endereço para navegar.</p>
                        )}
                    </div>

                    {/* Onde é, no mapa — e como consertar quando o pino não existe ou está longe. */}
                    {!visitada && (
                        <LocalizacaoParada
                            parada={parada}
                            cadastro={cadastroMapa ?? cadastro}
                            enderecoTexto={endereco}
                            corrigindo={corrigindoLocal}
                            onCorrigindo={v => onCorrigindoLocal?.(v)}
                            onSalvo={onRegistrado}
                        />
                    )}

                    {/* Contrato e documentos — o cliente costuma dizer que nunca
                        assinou, e a DATA e o que encerra a conversa. */}
                    {(termo || (cadastro?.documentos_json || []).length > 0 || parada.termo_situacao) && (
                        <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 space-y-1.5">
                            {/* O que a conferência respondeu sobre o termo, quando esta tela
                                não o achou sozinha. Aqui só se olha a UC e a negociação
                                vinculada; a conferência também procura pelo CPF. */}
                            {!termo && parada.termo_situacao === 'tem' && parada.termo_url && (
                                <a href={parada.termo_url} target="_blank" rel="noopener noreferrer"
                                    className="flex items-start gap-1.5 text-[11px] font-semibold text-[#5F6C37] hover:underline">
                                    <FileText size={12} className="shrink-0 mt-0.5" />
                                    <span>Termo de adesão</span>
                                </a>
                            )}
                            {!termo && parada.termo_situacao === 'anexado' && parada.termo_url && (
                                <button onClick={() => void abrirAnexo(parada.termo_url!)}
                                    className="flex items-start gap-1.5 text-[11px] font-semibold text-[#5F6C37] hover:underline text-left">
                                    <Paperclip size={12} className="shrink-0 mt-0.5" />
                                    <span>Termo de adesão (anexado na conferência)</span>
                                </button>
                            )}
                            {!termo && parada.termo_situacao === 'sem_termo' && (
                                <p className="flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
                                    <FileText size={12} className="shrink-0 mt-0.5" />
                                    <span>Sem termo de adesão — conferido antes da rota sair. Não há contrato assinado para mostrar.</span>
                                </p>
                            )}
                            {termo && (
                                <a
                                    href={termo.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-start gap-1.5 text-[11px] font-semibold text-[#5F6C37] hover:underline"
                                >
                                    <FileText size={12} className="shrink-0 mt-0.5" />
                                    <span>
                                        {termo.dataAssinatura
                                            ? `Termo assinado em ${dataBr(termo.dataAssinatura)}`
                                            : 'Termo de adesão'}
                                        {termo.numero ? ` (nº ${termo.numero})` : ''}
                                        {/* Assinou, mas a biometria do SERPRO nao voltou: dizer
                                            "verificado" aqui seria afirmar o que nao se sabe. */}
                                        {termo.verificada === false && ' — biometria pendente'}
                                    </span>
                                </a>
                            )}
                            {(cadastro?.documentos_json || []).map((d, i) => (
                                d?.url ? (
                                    <a
                                        key={`${d.url}-${i}`}
                                        href={d.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-start gap-1.5 text-[11px] text-gray-600 dark:text-gray-300 hover:underline"
                                    >
                                        <FileText size={12} className="shrink-0 mt-0.5" />
                                        <span>{d.name || 'Documento'}</span>
                                    </a>
                                ) : null
                            ))}
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        {urlMaps && (
                            <a href={urlMaps} target="_blank" rel="noopener noreferrer"
                                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-colors">
                                <Navigation size={14} /> Navegar
                            </a>
                        )}
                        {telefones.length > 0 && (
                            <a href={`tel:${telefones[0]}`}
                                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 text-gray-700 dark:text-gray-200 text-xs font-bold transition-colors">
                                <Phone size={14} /> {formatarTelefoneBr(telefones[0])}
                            </a>
                        )}
                        {zap && (
                            <a href={zap} target="_blank" rel="noopener noreferrer"
                                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-xs font-bold transition-colors">
                                <MessageCircle size={14} /> WhatsApp
                            </a>
                        )}
                        {!visitada && (
                            <button onClick={() => setMostrandoParcelamento(true)}
                                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-xs font-bold transition-colors">
                                <CalendarDays size={14} /> Parcelamento
                            </button>
                        )}
                        {!visitada && (
                            <button onClick={() => setRemovendo(true)} disabled={salvando}
                                className="flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-bold transition-colors disabled:opacity-50">
                                <Trash2 size={14} /> Remover parada
                            </button>
                        )}
                    </div>

                    {/* Por que a rota saiu sem o material completo, nas palavras de quem
                        liberou. Sem isto o cobrador descobre a falta na porta e não sabe
                        se foi erro ou combinado. */}
                    {parada.material_situacao === 'justificado' && parada.material_justificativa && (
                        <div className="flex items-start gap-2 p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                            <CircleAlert size={14} className="text-amber-600 shrink-0 mt-0.5" />
                            <p className="text-[11px] text-amber-800 dark:text-amber-200 leading-snug">
                                <strong>Saiu sem o material completo.</strong> Motivo de quem liberou:
                                {' '}“{parada.material_justificativa}”
                            </p>
                        </div>
                    )}

                    {/* Acordo de parcelamento: se existe, se o termo foi assinado, se
                        a entrada foi paga e como estão as parcelas. */}
                    {leitura && <AcordosDaParada dados={acordos} erro={erroAcordos} />}

                    {/* Faturas e meios de pagamento */}
                    {faturas && faturas.length > 0 && (
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider">
                                    Faturas vencidas em aberto ({faturas.length})
                                </p>
                                {/* O aviso de cabeçalho: quantas faturas estão sem o par
                                    boleto + fatura da Equatorial. Sem ele, quem está na porta
                                    do cliente descobre uma linha por vez. */}
                                {!avaliacaoMaterial.completo && (
                                    <span
                                        title={resumoPendencia(avaliacaoMaterial)}
                                        className={`flex items-center gap-1 text-[10px] font-bold ${
                                            avaliacaoMaterial.faltaNaRecente
                                                ? 'text-red-600 dark:text-red-400'
                                                : 'text-amber-600 dark:text-amber-400'}`}
                                    >
                                        <CircleAlert size={11} />
                                        {avaliacaoMaterial.pendencias.length} sem material
                                    </span>
                                )}
                            </div>
                            {faturas.map(f => (
                                <LinhaFatura
                                    key={f.id}
                                    fatura={f}
                                    mostrarUc={ucsDaParada(parada).length > 1}
                                    anexo={parada.material_anexos?.[String(f.id)] ?? null}
                                    papel={papelNoAcordo.get(String(f.id)) ?? null}
                                    onCopiar={copiar}
                                    onAbrirAnexo={abrirAnexo}
                                />
                            ))}
                        </div>
                    )}
                    {faturas && faturas.length === 0 && !carregandoDetalhe && (
                        <p className="text-[11px] text-green-700 dark:text-green-400 font-semibold">
                            {leitura?.outras.some(b => b.status === 'Parcelado')
                                ? 'Nenhuma fatura vencida agora — as que estavam em aberto foram parceladas (veja o acordo acima).'
                                : 'Nenhuma fatura vencida agora — pode ter sido paga depois que a rota foi montada.'}
                        </p>
                    )}

                    {/* Os outros boletos da UC — pagos, parcelados, a vencer —, para
                        responder na porta o "eu já paguei". */}
                    {leitura && leitura.outras.length > 0 && (
                        <OutrosBoletos
                            boletos={leitura.outras}
                            papel={papelNoAcordo}
                            mostrarUc={ucsDaParada(parada).length > 1}
                        />
                    )}

                    {/* Histórico de tratativas */}
                    {!verHistorico ? (
                        <button onClick={carregarHistorico}
                            className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500 hover:text-[#5F6C37] transition-colors">
                            <History size={13} /> Ver o que já foi tentado
                        </button>
                    ) : (
                        <div className="space-y-1.5">
                            <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Histórico</p>
                            {timeline === null && <p className="text-[11px] text-gray-400">Carregando...</p>}
                            {timeline?.length === 0 && (
                                <p className="text-[11px] text-gray-400">
                                    {parada.cpf_cnpj_cliente ? 'Nada registrado ainda.' : 'Cliente sem CPF/CNPJ: não há histórico ligado.'}
                                </p>
                            )}
                            {(timeline || []).slice(0, 6).map((ev, i) => (
                                <div key={i} className="text-[11px] leading-snug border-l-2 border-gray-200 dark:border-gray-600 pl-2">
                                    <span className="font-semibold text-gray-700 dark:text-gray-200">{ev.titulo}</span>
                                    <span className="text-gray-400"> · {dataBr(String(ev.em || '').slice(0, 10))}</span>
                                    {ev.detalhe && <div className="text-gray-500">{ev.detalhe}</div>}
                                    {ev.autor && <div className="text-gray-400">— {ev.autor}</div>}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Registro da visita */}
                    {visitada ? (
                        <div className="p-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            <p className="text-[11px] font-bold text-gray-700 dark:text-gray-200">
                                {parada.status === 'pulado' ? 'Parada pulada' : 'Visita registrada'}
                                {dataDaVisita && ` · ${dataDaVisita}`}
                                {parada.resultado && ` · ${rotuloResultado(parada.resultado)}`}
                            </p>
                            {parada.observacao && (
                                <p className="text-[11px] text-gray-600 dark:text-gray-300 mt-1 leading-snug">{parada.observacao}</p>
                            )}
                            {/* Pedido de 24/09/2026: voltar a uma porta já batida — o
                                cliente pediu para passar de novo, ou o registro foi por engano. */}
                            <button onClick={() => void reabrir()} disabled={mexendo !== null}
                                className="mt-2 flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#5F6C37] text-[#5F6C37] dark:border-[#7a8c47] dark:text-[#7a8c47] hover:bg-[#5F6C37]/5 text-xs font-bold transition-colors disabled:opacity-50">
                                {mexendo === 'reabrir' ? <Loader2 size={13} className="animate-spin" /> : <RotateCcw size={13} />}
                                Reabrir para visitar de novo
                            </button>
                        </div>
                    ) : !registrando ? (
                        <button onClick={() => setRegistrando(true)}
                            className="w-full py-3 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-sm font-bold transition-colors">
                            Registrar visita
                        </button>
                    ) : (
                        <div className="space-y-2 p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
                            <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider">O que aconteceu?</p>
                            <div className="grid grid-cols-2 gap-1.5">
                                {RESULTADOS_VISITA.map(r => (
                                    <button
                                        key={r.valor}
                                        onClick={() => setResultado(r.valor)}
                                        className={`py-2 px-2 rounded-lg text-[11px] font-bold transition-colors ${resultado === r.valor
                                            ? 'bg-[#5F6C37] text-white'
                                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
                                    >
                                        {r.rotulo}
                                    </button>
                                ))}
                            </div>
                            <textarea
                                value={observacao}
                                onChange={e => setObservacao(e.target.value)}
                                rows={3}
                                placeholder="O que foi tratado? (obrigatório)"
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 dark:bg-gray-800 text-sm outline-none focus:ring-2 focus:ring-[#5F6C37]"
                            />
                            {reagendando ? (
                                <div className="space-y-2 p-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                                    <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider">Voltar quando?</p>
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {[
                                            { rotulo: 'Mais tarde hoje', data: hoje },
                                            { rotulo: 'Amanhã', data: diasDepois(1) },
                                            { rotulo: 'Semana que vem', data: diasDepois(7) },
                                        ].map(op => (
                                            <button
                                                key={op.rotulo}
                                                onClick={() => setDataVolta(op.data)}
                                                aria-pressed={dataVolta === op.data}
                                                className={`py-2 px-1.5 rounded-lg text-[11px] font-bold leading-tight transition-colors ${dataVolta === op.data
                                                    ? 'bg-[#5F6C37] text-white'
                                                    : 'bg-gray-50 dark:bg-gray-900 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700'}`}
                                            >
                                                {op.rotulo}
                                            </button>
                                        ))}
                                    </div>
                                    <label className="flex items-center gap-2 text-[11px] text-gray-500">
                                        <span className="shrink-0">Outro dia:</span>
                                        <input
                                            type="date"
                                            min={hoje}
                                            value={dataVolta}
                                            onChange={e => setDataVolta(e.target.value)}
                                            className="flex-1 min-w-0 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm outline-none focus:ring-2 focus:ring-[#5F6C37]"
                                        />
                                    </label>
                                    <p className="text-[11px] text-gray-500 leading-snug">
                                        A visita entra no histórico agora. A parada fica na rota, vai para o fim da fila
                                        {dataVolta > hoje ? ' e some da lista até o dia marcado.' : '.'}
                                    </p>
                                    <div className="flex gap-2">
                                        <button onClick={() => setReagendando(false)} disabled={salvando}
                                            className="px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 text-xs font-bold disabled:opacity-50">
                                            Voltar
                                        </button>
                                        <button onClick={() => registrar('reagendar')} disabled={salvando || !dataVolta}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-bold transition-colors disabled:opacity-50">
                                            {salvando ? <Loader2 size={14} className="animate-spin" /> : <CalendarClock size={14} />}
                                            {!dataVolta ? 'Escolha o dia'
                                                : dataVolta === hoje ? 'Reagendar para mais tarde'
                                                    : `Reagendar para ${dataBr(dataVolta)}`}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="grid grid-cols-2 gap-2">
                                        <button onClick={() => registrar('pulado')} disabled={salvando}
                                            className="flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 text-xs font-bold disabled:opacity-50">
                                            <SkipForward size={13} /> Pular
                                        </button>
                                        <button onClick={() => { setDataVolta(diasDepois(1)); setReagendando(true); }} disabled={salvando}
                                            className="flex items-center justify-center gap-1 px-3 py-2.5 rounded-xl border border-[#5F6C37] text-[#5F6C37] dark:border-[#7a8c47] dark:text-[#7a8c47] text-xs font-bold disabled:opacity-50">
                                            <CalendarClock size={13} /> Reagendar
                                        </button>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={fecharRegistro}
                                            className="px-3 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200 text-xs font-bold">
                                            Cancelar
                                        </button>
                                        <button onClick={() => registrar('visitado')} disabled={salvando}
                                            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-[#5F6C37] hover:bg-[#4a5429] text-white text-xs font-bold transition-colors disabled:opacity-50">
                                            {salvando ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                                            Concluir visita
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            <ConfirmModal
                isOpen={removendo}
                onClose={() => { if (!salvando) fecharRemocao(); }}
                onConfirm={handleRemoverParada}
                title="Remover parada"
                message={`A parada ${parada.posicao} (${parada.cliente_nome || `UC ${parada.uc_codigo}`}) sai da rota e as seguintes sobem uma posição.`}
                confirmLabel="Remover"
                loading={salvando}
                confirmDisabled={!motivoRemocao.trim()}
            >
                <label className="block text-xs font-bold text-gray-600 dark:text-slate-300 mb-1">
                    Por que está saindo? (obrigatório)
                </label>
                <textarea
                    autoFocus
                    value={motivoRemocao}
                    onChange={e => setMotivoRemocao(e.target.value)}
                    rows={3}
                    placeholder="Ex.: incluída por engano, cliente já pagou..."
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:ring-2 focus:ring-red-500"
                />
            </ConfirmModal>

            {ucAberta && (
                <React.Suspense fallback={null}>
                    <UCManagementModal
                        isOpen
                        unidade={ucAberta}
                        abaInicial="dados_unidade"
                        onClose={fecharUc}
                        onSave={fecharUc}
                    />
                </React.Suspense>
            )}

            {mostrandoParcelamento && (
                <CriarParcelamentoModal
                    isOpen={mostrandoParcelamento}
                    onClose={() => setMostrandoParcelamento(false)}
                    onSuccess={() => setVersaoAcordos(v => v + 1)}
                    preSelectedUc={{ id: parada.unidade_consumidora_id, numero_uc: parada.uc_codigo, nome_cliente: parada.cliente_nome }}
                    preSelectedFaturas={faturas ?? undefined}
                />
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------

/**
 * Uma fatura e o que existe dela para o cliente pagar na hora.
 *
 * Medido em 10/09/2026: só 45% das faturas vencidas têm algum meio de pagamento
 * gravado. Por isso a linha mostra o que tem e diz claramente quando não tem
 * nada — mandar o cobrador procurar um boleto inexistente é pior que avisar.
 */
const LinhaFatura: React.FC<{
    fatura: FaturaEmAberto;
    /** Caminho do PDF da Equatorial anexado na conferência, quando o sistema não tinha. */
    anexo?: string | null;
    /** A parada junta várias UCs: a linha diz de qual é a fatura. */
    mostrarUc?: boolean;
    /** O papel da fatura num acordo vivo ("Na proposta de acordo…", "Parcela 2/10…"). */
    papel?: Etiqueta | null;
    onCopiar: (texto: string, oque: string) => void;
    onAbrirAnexo: (caminho: string) => void;
}> = ({ fatura, anexo, mostrarUc, papel, onCopiar, onAbrirAnexo }) => {
    const [abrindo, setAbrindo] = useState(false);
    const situacao = etiquetaDoStatus('Vencido', { vencimento: fatura.vencimento }, new Date());

    const abrirBoleto = async () => {
        setAbrindo(true);
        try {
            const url = await resolverLinkBoleto(fatura as any);
            if (url) window.open(url, '_blank');
        } finally { setAbrindo(false); }
    };

    const temBoleto = !!(fatura.url_boleto || fatura.url_boleto_combinado || fatura.pdf_boleto_combinado);
    const semNada = !temBoleto && !fatura.linha_digitavel_boleto && !fatura.codigo_pix_boleto;

    // Precisa dos DOIS: o boleto para receber e a fatura da Equatorial para
    // provar a dívida na porta. `faltasDaFatura` é a mesma regra usada na
    // conferência — uma fonte só, senão as duas telas divergem.
    const faltas = faltasDaFatura(fatura, anexo ? { [String(fatura.id)]: anexo } : null);

    return (
        <div className={`p-2.5 rounded-lg border ${
            faltas.length > 0
                ? 'bg-amber-50/60 dark:bg-amber-900/10 border-amber-200 dark:border-amber-900/40'
                : 'bg-gray-50 dark:bg-gray-900/40 border-gray-100 dark:border-gray-700'}`}>
            <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold text-gray-700 dark:text-gray-200 tabular-nums flex items-center gap-1.5">
                    {faltas.length > 0 && (
                        <CircleAlert
                            size={12}
                            className="text-amber-600 dark:text-amber-400 shrink-0"
                            aria-label={`Falta ${faltas.map(f => ROTULO_FALTA[f]).join(' e ')}`}
                        />
                    )}
                    {mostrarUc && fatura.uc_ref ? <span className="text-gray-400">UC {fatura.uc_ref} ·</span> : null}
                    {fatura.mes_referencia || '—'} · venc. {dataBr(fatura.vencimento)}
                </span>
                <span className="text-[11px] font-black text-gray-800 dark:text-gray-100 tabular-nums">{moeda(fatura.valor)}</span>
            </div>
            <p className="mt-0.5 text-[10px] font-bold leading-snug">
                <span className={COR_TOM[situacao.tom].icone}>{situacao.rotulo}</span>
                {papel ? <span className={COR_TOM[papel.tom].icone}> · {papel.rotulo}</span> : null}
            </p>
            {faltas.length > 0 && (
                <p className="mt-1 text-[10px] font-bold text-red-700 dark:text-red-400">
                    Falta {faltas.map(f => ROTULO_FALTA[f]).join(' e ')}
                </p>
            )}
            <div className="mt-1.5 flex flex-wrap gap-1.5">
                {temBoleto && (
                    <button onClick={abrirBoleto} disabled={abrindo}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[10px] font-bold text-gray-600 dark:text-gray-300 disabled:opacity-50">
                        {abrindo ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />} Boleto
                    </button>
                )}
                {fatura.codigo_pix_boleto && (
                    <button onClick={() => onCopiar(fatura.codigo_pix_boleto!, 'PIX')}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[10px] font-bold text-gray-600 dark:text-gray-300">
                        <Copy size={11} /> PIX
                    </button>
                )}
                {fatura.linha_digitavel_boleto && (
                    <button onClick={() => onCopiar(fatura.linha_digitavel_boleto!, 'Linha digitável')}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[10px] font-bold text-gray-600 dark:text-gray-300">
                        <Copy size={11} /> Linha digitável
                    </button>
                )}
                {fatura.pdf_fatura && (
                    <a href={fatura.pdf_fatura} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[10px] font-bold text-gray-600 dark:text-gray-300">
                        <FileText size={11} /> Fatura Equatorial
                    </a>
                )}
                {!fatura.pdf_fatura && anexo && (
                    <button onClick={() => onAbrirAnexo(anexo)}
                        className="flex items-center gap-1 px-2 py-1 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-[10px] font-bold text-gray-600 dark:text-gray-300">
                        <Paperclip size={11} /> Fatura Equatorial (anexo)
                    </button>
                )}
                {semNada && (
                    <span className="text-[10px] text-amber-600 font-semibold">
                        Sem boleto emitido — emita antes de cobrar.
                    </span>
                )}
            </div>
        </div>
    );
};

/** Quantos boletos resolvidos aparecem antes do "Ver todos". */
const OUTROS_BOLETOS_VISIVEIS = 6;

/**
 * Os boletos da UC que não estão vencidos, dos últimos 12 meses: pagos (com a
 * data), quitados, parcelados, cancelados e a vencer. É o que responde, na
 * porta, o "eu já paguei" — antes era abrir Faturas em outra aba.
 */
const OutrosBoletos: React.FC<{
    boletos: FaturaDaParada[];
    papel: Map<string, Etiqueta>;
    mostrarUc: boolean;
}> = ({ boletos, papel, mostrarUc }) => {
    const [todos, setTodos] = useState(false);
    const hoje = new Date();
    const pagos = boletos.filter(b => b.status === 'Pago' || b.status === 'Quitado').length;
    const visiveis = todos ? boletos : boletos.slice(0, OUTROS_BOLETOS_VISIVEIS);
    return (
        <div className="space-y-1.5">
            <p className="text-[10px] font-black uppercase text-gray-400 tracking-wider">
                Outros boletos · últimos 12 meses
                <span className="ml-1 normal-case tracking-normal font-semibold">({pagos} pago{pagos === 1 ? '' : 's'})</span>
            </p>
            <ul className="rounded-lg border border-gray-100 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
                {visiveis.map(b => {
                    const e = etiquetaDoStatus(b.status, { vencimento: b.vencimento, pagoEm: b.data_pgto_boleto, quitadoEm: b.quitado_em }, hoje);
                    const p = papel.get(b.id);
                    return (
                        <li key={b.id} className="px-2.5 py-1.5 flex items-center gap-2">
                            <span className="flex-1 min-w-0 text-[11px] tabular-nums">
                                <span className="font-bold text-gray-700 dark:text-gray-200">
                                    {mostrarUc && b.uc_ref ? <span className="font-normal text-gray-400">UC {b.uc_ref} · </span> : null}
                                    {b.mes_referencia || '—'}
                                </span>
                                <span className="text-gray-500 dark:text-gray-400"> · {moeda(b.valor)}</span>
                                {p ? <span className={`block text-[10px] font-semibold ${COR_TOM[p.tom].icone}`}>{p.rotulo}</span> : null}
                            </span>
                            <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] leading-none font-black ring-1 ring-inset ${COR_TOM[e.tom].chip}`}>
                                {e.rotulo}
                            </span>
                        </li>
                    );
                })}
            </ul>
            {boletos.length > OUTROS_BOLETOS_VISIVEIS && (
                <button type="button" onClick={() => setTodos(v => !v)}
                    className="text-[11px] font-semibold text-gray-500 hover:text-[#5F6C37] transition-colors">
                    {todos ? 'Mostrar menos' : `Ver todos (${boletos.length})`}
                </button>
            )}
        </div>
    );
};

export default RotaCobrancaView;
